import type { IncomingMessage } from 'http';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  QuestionOutlineIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonProps,
  Center,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HeadingProps,
  HStack,
  IconButton,
  Image,
  ImageProps,
  Link,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Select,
  SimpleGrid,
  Spacer,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
  UnorderedList,
  useBreakpointValue,
  useClipboard,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Collapse } from '@chakra-ui/transition';
import isMobileDevice from 'ismobilejs';
import { sampleSize } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import NextHead from 'next/head';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, {
  ChangeEventHandler,
  Fragment,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HashNavigation, Keyboard, Lazy, Navigation, Scrollbar } from 'swiper';
import 'swiper/css';
import 'swiper/css/hash-navigation';
import 'swiper/css/keyboard';
import 'swiper/css/lazy';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperClass from 'swiper/types/swiper-class';
import * as yup from 'yup';
import { getPublicRuntimeConfig } from '../../../common/config';
import {
  ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT,
  MobileSpecialBar,
  Special,
  SpecialHasMobileBanner,
  URL_INQUIRY_FORM_MULTI,
} from '../../../common/constants';
import {
  Article,
  ArticleStatus,
  ArticleVideo,
  createArticleEditRelativeUrl,
  createArticleEmbedUrl,
  createArticleImageRelativeUrl,
  createArticleImageUrl,
  createArticleUrl,
  shouldNotShow,
  TableOfContents,
  User,
} from '../../../common/libs/article';
import { BadgeArgs } from '../../../common/libs/badgeType';
import {
  CommentDiscloseScope,
  commentDiscloseScope,
} from '../../../common/libs/commentDiscloseScope';
import type { CommentDisplayMode } from '../../../common/libs/commentDisplayMode';
import { commentDisplayMode } from '../../../common/libs/commentDisplayMode';
import type { DiscloseScope } from '../../../common/libs/discloseScope';
import { createShareUrl as createFacebookShareUrl } from '../../../common/libs/facebook';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { title } from '../../../common/libs/metadata';
import {
  findMobileSpecialBarByTags,
  findSpecialByTags,
  findSpecialByArticleId,
  findMobileSpecialBarByArticleId,
} from '../../../common/libs/specials';
import { createShareUrl as createTwitterShareUrl } from '../../../common/libs/twitter';
import {
  createUserName,
  isPharmaceuticalAccount,
  isDoctorRecruiterAccount,
} from '../../../common/libs/user';
import {
  hasDemoAuthority,
  hasManageAuthority,
} from '../../../common/libs/userRoleType';
import AchievedBadgeModal from '../../../components/AchievedBadgeModal';
import ArticleComments from '../../../components/ArticleComments';
import ArticleMetrics from '../../../components/ArticleMetrics';
import ArticleRelatedSlidesPage from '../../../components/ArticleRelatedSlidesPage';
import ArticlesGrid, { ArticleHeader } from '../../../components/ArticlesGrid';
import DepartmentBadgeLink from '../../../components/DepartmentBadgeLink';
import ExternalLink from '../../../components/ExternalLink';
import {
  FaLink,
  FaLock,
  FaXTwitter,
  HiCheckBadge,
  IoBookmark,
  IoEyeOutline,
  MdBookmarkAdded,
  MdContentCopy,
  MdFacebook,
  MdIosShare,
  MdOpenInFull,
  MdOutlineBookmarkAdd,
  MdOutlineChat,
  MdOutlineCloseFullscreen,
} from '../../../components/Icons';
import { JsonLd } from '../../../components/JsonLd';
import LinkifyText from '../../../components/LinkifyText';
import MobileAppDownloadBar from '../../../components/MobileAppDownloadBar';
import { ProfileImage } from '../../../components/ProfileImage';
import TagButtonLink from '../../../components/TagButtonLink';
import { useAppContext } from '../../../contexts/app';
import useApiClient from '../../../hooks/useApiClient';
import useAppRouter from '../../../hooks/useAppRouter';
import useArticleComment from '../../../hooks/useArticleComment';
import useArticleLike from '../../../hooks/useArticleLike';
import useFullscreen from '../../../hooks/useFullscreen';
import useIdApiClient from '../../../hooks/useIdApiClient';
import usePageLike from '../../../hooks/usePageLike';
import * as date from '../../../server/libs/date';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma, { Prisma } from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { AntaaAccountSubType } from '../../../common/libs/antaaAccountSubType';

const MAX_AUTHOR_OTHER_ARTICLES_COUNT = 3;
const MAX_SAME_TAG_ARTICLES_COUNT = 9;
const MAX_SAME_DEPARTMENT_ARTICLES_COUNT = 9;
const MAX_RELATED_ARTICLES_COUNT = 9;
const MAX_RELATED_ARTICLES_MOBILE_COUNT = 4;

// NOTE: リダイレクト先URL
// see https://docs.google.com/spreadsheets/d/1R9RwlQVLqbn7ovGTcCosYG3qFSj06tSkwMt8EhkWKvs/edit#gid=0
const redirectPageMap = new Map<string, string>([
  ['2ec28873c5794b3f', '31e125b91cb6401f'],
  ['9e0c56991c28459b', 'c5d22e1f19d34bee'],
  ['1aa9ed8d10764a35', '17fca2dfc24c46fc'],
  ['bb5221f76b53481c', 'e6d1c1d825b0422b'],
  ['55c9fe3d60e64968', '4ecf61e1161349da'],
  ['b1a163c0472f4913', '48d1e1a8afc74acb'],
  ['1dcc002a743645cc', '05aeb7c412bf4b3c'],
  ['c9c21ad1c6504dd5', '8f93464b1ba545d3'],
  ['0308e3753770414b', '866bd5db4708459d'],
  ['2c920840ebe5461f', '96209282eeba4d7f'],
  ['10acc1cdae114b1c', 'bac95cf148d74099'],
  ['e71f4a9b005045c7', '57a389d3c600444c'],
  ['85e5a73487574deb', 'bac1e5bbd1a940cd'],
  ['e9e0ad5fa56d4d3d', 'ea4ca81978a649bb'],
  ['5199820d537c41bc', '8f3fd318677946dc'],
]);

type Tag = {
  id: number;
  name: string | null;
};

type Department = {
  id: number;
  name: string;
};

type DepartmentWithArticlesCount = Department & {
  articlesCount: number;
};

type ArticlePage = {
  articleId: string;
  pageNumber: number;
  slideText: string | null;
  linkUrl: string | null;
  imgL: string;
};

type Author = User & {
  hospital: string | null;
  twitterAccount: string | null;
};

type Likes = {
  article: boolean;
  pages: boolean[];
};

type Award = {
  id: number;
  name: string;
  year: number;
  imageFilename: string;
};

type Interview = {
  id: number;
  siteUrl: string;
  imageUrl: string;
};

type TargetReaderType = {
  id: number;
  name: string;
};

type RichResultArticle = {
  headline: string;
  image: string[];
  datePublished: string; // ISO8601
  dateModified: string; // ISO8601
  authorId: number;
  authorDiscloseId: string | null;
  authorName: string;
};

type PageContext = {
  article: Article;
  tags: Tag[];
  author: Author;
  authorAwards: Award[];
  authorInterviews: Interview[];
  authorOtherArticles: ArticleHeader[];
  hasMoreAuthorOtherArticles: boolean;
  sameTagArticles: ArticleHeader[];
  sameDepartmentArticles: ArticleHeader[];
  likes: Likes;
  articleDepartments: Department[];
  allDepartments: DepartmentWithArticlesCount[];
  targetReaderTypes: TargetReaderType[];
  richResultArticle: RichResultArticle;
  isMobile: boolean;
  isAdminAccount: boolean;
  isDemoAccount: boolean;
  isPharmaceuticalAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isCorporationAccount: boolean;
  isAuthor: boolean;
  loginUser: { userId: number; discloseId: string | null } | null;
  nonPublic: boolean;
  shouldLoginInduction: boolean;
};

type LikeModalDisclosure = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const Breadcrumbs: React.FC<{ departments: Department[] }> = ({
  departments,
}) => {
  return (
    <Breadcrumb
      spacing="8px"
      separator={<ChevronRightIcon color="gray.500" />}
      fontSize="xs"
      opacity="0.8"
    >
      <BreadcrumbItem>
        <BreadcrumbLink
          href="/"
          onClick={() => sendAnalyticsEvent('breadcrumb', 'click', 'home')}
        >
          ホーム
        </BreadcrumbLink>
      </BreadcrumbItem>

      <BreadcrumbItem>
        {departments.map((d, idx) => {
          return (
            <Fragment key={d.id}>
              <BreadcrumbLink
                as={NextLink}
                href={`/department/${d.id}`}
                passHref
              >
                <Text
                  _hover={{
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    sendAnalyticsEvent('breadcrumb', 'click', 'department');
                    sendAnalyticsEvent(
                      'slide',
                      'index',
                      'frequently_used_department',
                    );
                  }}
                >
                  {d.name}
                </Text>
              </BreadcrumbLink>
              {idx < departments.length - 1 && <span>・</span>}
            </Fragment>
          );
        })}
      </BreadcrumbItem>
    </Breadcrumb>
  );
};

function JsonLdArticle({
  richResultArticle,
}: {
  richResultArticle: RichResultArticle;
}) {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const authorUrl = `${slideWebBaseUrl}/profile/${
    richResultArticle.authorDiscloseId || richResultArticle.authorId
  }`;
  const structuredData = {
    '@type': 'Article',
    headline: richResultArticle.headline,
    image: richResultArticle.image,
    datePublished: richResultArticle.datePublished,
    dateModified: richResultArticle.dateModified,
    author: [
      {
        '@type': 'Person',
        url: authorUrl,
        name: richResultArticle.authorName,
      },
    ],
  };
  return <JsonLd data={structuredData} />;
}

function JsonLdBreadcrumbList({
  departments,
  article,
}: {
  departments: Department[];
  article: Article;
}) {
  if (departments.length === 0) return null;
  const { slideWebBaseUrl } = getPublicRuntimeConfig();

  const createListItem = (department: Department) => ({
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: slideWebBaseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${department.name}`,
        item: `${slideWebBaseUrl}/department/${department.id}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${article.name}`,
      },
    ],
  });

  const structuredData =
    departments.length === 1
      ? createListItem(departments[0])
      : departments.map(createListItem);

  return <JsonLd data={structuredData} />;
}

function PageHead({
  author,
  article,
  richResultArticle,
  departments,
}: {
  author: Author;
  article: Article;
  richResultArticle: RichResultArticle;
  departments: Department[];
}) {
  const imgUrl = createArticleImageUrl(article.articleId, article.topImageL, {
    version: article.updateDateEpochMilliseconds,
  });
  const articleUrl = createArticleUrl(article.articleId);
  return (
    <>
      <NextHead>
        <title>{title(article.name ?? '')}</title>
        <link rel="canonical" href={articleUrl} />
        <meta name="description" content={article.description || undefined} />
        <meta
          property="og:title"
          content={`${article.name} | Antaa Slide`}
          key="og:title"
        />
        <meta property="og:type" content="article" key="og:type" />
        <meta
          property="og:image"
          content={imgUrl || undefined}
          key="og:image"
        />
        <meta property="og:url" content={articleUrl} key="og:url" />
        <meta
          property="og:description"
          content={article.description || undefined}
          key="og:description"
        />
        <meta name="twitter:image" content={imgUrl || undefined} />
      </NextHead>
      {/* SEE: https://github.com/vercel/next.js/issues/22729 */}
      <JsonLdArticle richResultArticle={richResultArticle} />
      <JsonLdBreadcrumbList departments={departments} article={article} />
    </>
  );
}

function ArticleNameWithIcon({
  name,
  discloseScope = undefined,
}: {
  name: string | null;
  discloseScope?: DiscloseScope | null;
}) {
  const length = useBreakpointValue(['1.25rem', '1.875rem']); // same as fontSize of Heading
  const lockStyleProps = {
    minWidth: length,
    minHeight: length,
  };

  return (
    <Box mt={2} display="inline-flex" gap={1} maxW="100%">
      {discloseScope && discloseScope === 'PARTIAL' && (
        <FaLock color="#D6BF51" style={lockStyleProps} />
      )}
      <Heading
        as="h1"
        fontSize={['xl', '3xl']} // ['1.25rem', '1.875rem'] (chakra-ui default)
        overflow="hidden"
      >
        {name}
      </Heading>
    </Box>
  );
}

function SectionHeading(props: HeadingProps) {
  return <Heading fontSize="xl" {...props} />;
}

function ArticleEditButton({
  article,
  isPharmaceuticalAccount,
  ...buttonProps
}: { article: Article; isPharmaceuticalAccount: boolean } & ButtonProps) {
  const { user, isAuthenticated, isLicenseVerified, hasDemoAuthority } =
    useAppContext();
  if (
    !isAuthenticated ||
    article.authorId !== user?.id ||
    hasDemoAuthority ||
    isPharmaceuticalAccount
  ) {
    return null;
  }
  if (
    article.discloseScope !== 'FULL' &&
    isAuthenticated &&
    !isLicenseVerified
  ) {
    return null;
  }
  const url = createArticleEditRelativeUrl(article.articleId);
  return (
    <NextLink href={url} passHref>
      <Button as="a" size="sm" variant="outline" {...buttonProps}>
        スライド情報を編集
      </Button>
    </NextLink>
  );
}

function SlideImage({
  lazy = true,
  src,
  alt = '',
  ...rest
}: { lazy?: boolean } & ImageProps) {
  return (
    <Image
      className="swiper-lazy"
      src={lazy ? undefined : src}
      data-src={src}
      alt={lazy ? '' : alt}
      objectFit="contain"
      _after={{
        // HACK: broken imageを隠す
        // https://bitsofco.de/styling-broken-images/
        content: '""',
        d: 'block',
        bg: '#333',
        pos: 'absolute',
        top: 0,
        left: 0,
        w: '100%',
        h: '100%',
      }}
      {...rest}
    />
  );
}

function LinkableSlideImage({
  href,
  isExternal,
  ...imageProps
}: {
  href: string | null;
  isExternal: boolean | true;
} & React.ComponentPropsWithoutRef<typeof SlideImage>) {
  return (
    <>
      {!!href ? (
        <Link href={href} isExternal={isExternal}>
          <SlideImage {...imageProps} />
        </Link>
      ) : (
        <SlideImage {...imageProps} />
      )}
    </>
  );
}

function useSlidePageMaxSize() {
  const pageMaxHeightVariant =
    useBreakpointValue({
      base: '240px',
      sm: '300px',
      md: '360px',
      '2xl': '680px',
    }) || '360px';
  const heightToWidth = (heightVariant: string) => {
    return Number(heightVariant.replace(/[^0-9]/g, '')) * (1280 / 720); // アスペクト比は slide_require_account_signup.png に合わせた
  };

  const [pageMaxHeight, setPageMaxHeight] = useState(pageMaxHeightVariant);
  const [pageMaxWidth, setPageMaxWidth] = useState(
    heightToWidth(pageMaxHeightVariant),
  );

  useEffect(() => {
    setPageMaxHeight(pageMaxHeightVariant);
    setPageMaxWidth(heightToWidth(pageMaxHeightVariant));
  }, [pageMaxHeightVariant]);

  const setPageMaxHeightFull = useCallback(
    () => setPageMaxHeight('100%'),
    [setPageMaxHeight],
  );
  const setPageMaxHeightNormal = useCallback(
    () => setPageMaxHeight(pageMaxHeightVariant),
    [setPageMaxHeight, pageMaxHeightVariant],
  );

  return {
    pageMaxHeight,
    setPageMaxHeightFull,
    setPageMaxHeightNormal,
    pageMaxWidth,
  };
}

function SlideSwiperMenu({ article }: { article: Article }) {
  const articleUrl = createArticleUrl(article.articleId);
  const { hasCopied, onCopy: copyToClipboard } = useClipboard(articleUrl);
  return (
    <>
      <Menu placement="top-end" computePositionOnMount autoSelect={false}>
        <MenuButton
          as={IconButton}
          aria-label="シェア"
          icon={
            <>
              <VStack spacing={0}>
                <MdIosShare />
                <Text fontSize={['xx-small', 'xs']}>シェア</Text>
              </VStack>
            </>
          }
          variant="unstyled"
          w={[10, 16]}
          color="white"
          _hover={{
            bg: 'whiteAlpha.300',
          }}
          _focus={{
            boxShadow: 'none',
          }}
        />
        <MenuList padding={0} overflow="hidden" color="white" border="none">
          <>
            <FacebookShareMenuItem article={article} />
            <TwitterShareMenuItem article={article} />
          </>
          <MenuItem
            bg="blackAlpha.800"
            _hover={{
              bg: 'blackAlpha.700',
            }}
            onClick={() => {
              sendAnalyticsEvent('slide', 'share', 'copy_url_on_menu');
              copyToClipboard();
            }}
          >
            リンクをコピー
          </MenuItem>
          <EmbedMenuItem article={article} />
        </MenuList>
      </Menu>
      <Popover isOpen={hasCopied} placement="bottom-end" autoFocus={false}>
        <PopoverTrigger>
          <Box w={0} role="button" aria-label="コピーしました" />
        </PopoverTrigger>
        <PopoverContent>
          <PopoverBody>コピーしました</PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
}

function FacebookShareMenuItem({ article }: { article: Article }) {
  const articleUrl = createArticleUrl(article.articleId);
  const shareUrl = createFacebookShareUrl({ url: articleUrl });
  return (
    <MenuItem
      as="a"
      href={shareUrl}
      target="_blank"
      rel="noopener noreferrer"
      bg="blackAlpha.800"
      _hover={{
        bg: 'blackAlpha.700',
      }}
      onClick={() => {
        sendAnalyticsEvent('slide', 'share', 'facebook_on_menu');
      }}
    >
      Facebookでシェア
    </MenuItem>
  );
}

function TwitterShareMenuItem({ article }: { article: Article }) {
  const articleUrl = createArticleUrl(article.articleId);
  const text = `${article.name} | Antaa Slide @antaa__jp #Antaa`;
  const shareUrl = createTwitterShareUrl({
    text,
    url: articleUrl,
  });
  return (
    <MenuItem
      as="a"
      href={shareUrl}
      target="_blank"
      rel="noopener noreferrer"
      bg="blackAlpha.800"
      _hover={{
        bg: 'blackAlpha.700',
      }}
      onClick={() => {
        sendAnalyticsEvent('slide', 'share', 'twitter_on_menu');
      }}
    >
      Xでシェア
    </MenuItem>
  );
}

function EmbedMenuItem({ article }: { article: Article }) {
  const {
    isOpen: isOpenEmbedModal,
    onOpen: onOpenEmbedModal,
    onClose: onCloseEmbedModal,
  } = useDisclosure();
  return (
    <>
      <MenuItem
        display={['none', 'block']}
        bg="blackAlpha.800"
        _hover={{
          bg: 'blackAlpha.700',
        }}
        onClick={() => {
          sendAnalyticsEvent('slide', 'share', 'embed_on_menu');
          onOpenEmbedModal();
        }}
      >
        スライドを埋め込む
      </MenuItem>
      <EmbedModal
        article={article}
        isOpen={isOpenEmbedModal}
        onClose={onCloseEmbedModal}
      />
    </>
  );
}

function FullscreenButton({
  activated,
  ...props
}: ButtonProps & { activated: boolean }) {
  return (
    <IconButton
      aria-label="フルスクリーンで表示"
      icon={
        activated ? (
          <MdOutlineCloseFullscreen style={{ width: 20, height: 20 }} />
        ) : (
          <MdOpenInFull style={{ width: 20, height: 20 }} />
        )
      }
      color="white"
      bg="blackAlpha.600"
      _hover={{
        bg: 'blackAlpha.800',
      }}
      size="md"
      {...props}
    />
  );
}

function SlideSwiper({
  article,
  author,
  swiperOptions,
  likes,
  activeIndex,
  setActiveIndex,
  onClickPageLike,
  onClickPageUnlike,
  isDemoAccount,
  isPharmaceuticalAccount,
  isDoctorRecruiterAccount,
  relatedArticles,
  getArticleCommentCount,
  fetchArticleComments,
  postArticleComment,
  achievedBadgeModalOnOpen,
  setBadge,
}: {
  article: Article;
  author: Author;
  swiperOptions: { onHashSet: (swiper: SwiperClass) => void };
  likes: Likes;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onClickPageLike: (pageNum: number) => void;
  onClickPageUnlike: (pageNum: number) => void;
  isDemoAccount: boolean;
  isPharmaceuticalAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  relatedArticles: ArticleHeader[];
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  postArticleComment: (
    message: string,
    discloseScope?: CommentDiscloseScope,
    pageNumber?: number,
  ) => Promise<Array<BadgeArgs>>;
  achievedBadgeModalOnOpen: () => void;
  setBadge: React.Dispatch<React.SetStateAction<BadgeArgs | undefined>>;
}) {
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const { apiClient } = useApiClient();
  const { proceedIdPage } = useIdApiClient();
  const {
    isFullscreenEnabled,
    isFullscreenActive,
    toggleFullscreen,
    addFullscreenChangeListener,
    removeFullscreenChangeListener,
  } = useFullscreen();

  const loginUrl = useMemo(() => {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}#${
        ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT + 1
      }`,
    );
    return `/login?redirectUrl=${redirectUrl}`;
  }, [article.articleId]);
  const signupUrl = useMemo(() => {
    const { idBaseUrl } = getPublicRuntimeConfig();
    const accountSignupUrl = `${idBaseUrl}/signup?from_service=slide`;
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}#${
        ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT + 1
      }`,
    );
    return `${accountSignupUrl}&redirect_url=${redirectUrl}`;
  }, [article.articleId]);
  const articlePages = useMemo(
    () =>
      article.discloseScope === 'PARTIAL' &&
      (!isAuthenticated || !isLicenseVerified)
        ? article.pages.slice(0, ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT)
        : article.pages,
    [article.discloseScope, article.pages, isAuthenticated, isLicenseVerified],
  );

  const shouldShowLoginInduction =
    !isAuthenticated && article.discloseScope === 'PARTIAL';
  const shouldShowLicenseVerifyPage =
    article.discloseScope === 'PARTIAL' &&
    isAuthenticated &&
    !isLicenseVerified;
  const shouldShowRelatedSlidesPage =
    !shouldShowLoginInduction && !shouldShowLicenseVerifyPage;

  const shouldShowQuestionAndPageLikeButtons =
    !isDemoAccount && !isPharmaceuticalAccount;

  const incrementSessionCount = useCallback(
    async (articleId: string, pageNo: number) => {
      await apiClient(`/api/article/count-view/${articleId}/${pageNo}`).catch(
        (_e) => {},
      );
    },
    [apiClient],
  );

  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  const onSwiper = useCallback(
    (swiper: SwiperClass) => {
      setSwiper(swiper);
      setActiveIndex(swiper.activeIndex);
      if (swiper.activeIndex === 0) {
        // activeIndexが0以外の場合は、onActiveIndexChangeが先に呼ばれ、incrementSessionCount実行済み。
        incrementSessionCount(article.articleId, swiper.activeIndex + 1);
      }
    },
    [setActiveIndex, incrementSessionCount, article.articleId],
  );

  const onActiveIndexChange = useCallback(
    (swiper: SwiperClass) => {
      setActiveIndex(swiper.activeIndex);
      incrementSessionCount(article.articleId, swiper.activeIndex + 1);
    },
    [setActiveIndex, incrementSessionCount, article.articleId],
  );

  const slideTo = useCallback(
    (index: number) => {
      swiper?.slideTo(index);
    },
    [swiper],
  );

  const {
    pageMaxHeight,
    setPageMaxHeightFull,
    setPageMaxHeightNormal,
    pageMaxWidth,
  } = useSlidePageMaxSize();

  const fullscreenActiveRef = useRef(isFullscreenActive);

  useEffect(() => {
    fullscreenActiveRef.current = isFullscreenActive;
  }, [isFullscreenActive]);

  /** フルスクリーンモードの変更時にスライドページのサイズを調整 */
  const onFullscreenChange = useCallback(() => {
    // ここで isFullscreenActive を使用すると、fullscreenモードが切り替わった際に,
    // onFullscreenChange のアップデートと、それに連なる下記のuseEffectが再実行され、Eventが上手く発火しないため、
    // isFullscreenActiveの状態をrefに格納してそれを参照することにより影響を受けないようにしている
    const isActive = fullscreenActiveRef.current;
    if (isActive) {
      setPageMaxHeightFull();
    } else {
      setPageMaxHeightNormal();
    }
  }, [setPageMaxHeightFull, setPageMaxHeightNormal]);

  /** フルスクリーンモードの変更を監視し、event listenerを追加・削除する */
  useEffect(() => {
    addFullscreenChangeListener(onFullscreenChange);
    return () => {
      removeFullscreenChangeListener(onFullscreenChange);
    };
  }, [
    addFullscreenChangeListener,
    onFullscreenChange,
    removeFullscreenChangeListener,
  ]);

  const fullscreenRef = useRef<HTMLDivElement>(null);
  const onClickFullscreenButton: MouseEventHandler<HTMLButtonElement> = (e) => {
    const gaEventLabel = isFullscreenActive ? 'close' : 'expand';
    sendAnalyticsEvent('slide', 'full_screen', gaEventLabel);
    if (fullscreenRef.current) {
      toggleFullscreen(fullscreenRef.current);
    }
    e.currentTarget.blur();
  };

  const [windowWidth, setWindowWidth] = useState(0);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loginButtonSize = useBreakpointValue(['sm', 'md', 'lg', 'lg']);

  let pageCount = article.pageCount;
  if (shouldShowLoginInduction) {
    pageCount += 1;
  }
  if (shouldShowRelatedSlidesPage) {
    pageCount += 1;
  }
  if (shouldShowLicenseVerifyPage) {
    pageCount += 1;
  }

  // スライド解説音声ニーズ検証用articleID
  const audioArticles = [
    '0b3016c4d9a744dd',
    '9f63dc9b5e554e18',
    '6cdabed1d5f042e8',
    'de743c02c3214053',
    'e1e837d0ad0f4595',
    '067c1280b2894578',
    '082cc30a32574104',
    '506f6f7abf6a4f0e',
    '1e2ec4bfb1ef49e3',
    'aa2b2e57d13d45a3',
    'a56d0632ceef4e11',
  ];
  const audioUrl =
    shouldShowRelatedSlidesPage && audioArticles.includes(article.articleId)
      ? `/api/article/listen-audio/${article.articleId}/001.mp3` // 検証では001.mp3のみ
      : null;
  // audioタグのビルトインUIはサイズにより構成が変わるため画面サイズにより明示的にサイズ指定
  const audioWidth = useBreakpointValue({
    base: '200px', // iOSでスライダーが表示される最小幅
    sm: '250px',
    lg: '380px',
  });

  return (
    <Box>
      <Center ref={fullscreenRef} pos="relative">
        <Swiper
          slidesPerView={1}
          modules={[Navigation, Keyboard, Scrollbar, Lazy, HashNavigation]}
          preloadImages={false}
          navigation
          keyboard
          scrollbar={{ hide: false, draggable: true, snapOnRelease: true }}
          lazy={{
            loadPrevNext: true,
            loadOnTransitionStart: true,
            loadPrevNextAmount: 1,
          }}
          hashNavigation={{ replaceState: true, watchState: true }}
          onSwiper={onSwiper}
          onActiveIndexChange={onActiveIndexChange}
          onHashSet={swiperOptions.onHashSet}
          style={{
            background: 'rgba(0, 0, 0, 0.8)', // blackAlpha.800
            height: '100%',
            paddingTop: '4px',
            paddingBottom: '10px',
          }}
        >
          {articlePages.map((page, i) => (
            <SwiperSlide
              key={page.pageNumber}
              data-hash={page.pageNumber}
              style={{ width: isFullscreenActive ? '100vh' : undefined }}
            >
              <Center>
                <LinkableSlideImage
                  href={page.linkUrl}
                  isExternal={!article.needLogin}
                  h={isFullscreenActive ? '100vh' : pageMaxHeight}
                  w={isFullscreenActive ? undefined : pageMaxWidth}
                  src={`/api/article/view-image/${page.articleId}/${page.imgL}?v=${article.updateDateEpochMilliseconds}`}
                  lazy={i > 0}
                  alt={`${article.name} ${page.imgL}`}
                />
              </Center>
            </SwiperSlide>
          ))}
          {/* ログイン＆サインイン誘導ページ */}
          {shouldShowLoginInduction && (
            <SwiperSlide
              data-hash="nologin"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Center pos="relative">
                <NextLink href={loginUrl} passHref>
                  <Link title="アカウント登録はこちら">
                    <SlideImage
                      h={isFullscreenActive ? '100%' : pageMaxHeight}
                      w={isFullscreenActive ? '100%' : pageMaxWidth}
                      src="/images/slide_require_account_signup.png"
                      alt="アカウント登録はこちら"
                      onClick={() =>
                        sendAnalyticsEvent(
                          'induction',
                          'login',
                          'last_page_area',
                        )
                      }
                    />
                  </Link>
                </NextLink>
                <VStack
                  pos="absolute"
                  bottom={['10%', '3%', '8%', '9%']}
                  spacing={[1, 2]}
                  w={pageMaxWidth * 0.86}
                  maxWidth={windowWidth * 0.8}
                  flex="1"
                >
                  <NextLink href={signupUrl}>
                    {/* アカウント登録は事前のID基盤ログインが不要なためproceedIdPageを使わない */}
                    <Button
                      size={loginButtonSize}
                      flexShrink={0}
                      colorScheme="brand"
                      flex="1"
                      px={[4, 10]}
                      py={[2, 3]}
                      onClick={() =>
                        sendAnalyticsEvent('induction', 'signup', 'last_page')
                      }
                    >
                      <Text
                        color="white"
                        fontSize={['xs', 'md']}
                        fontWeight="bold"
                      >
                        続きを読む →
                      </Text>
                    </Button>
                  </NextLink>
                  <Flex>
                    <Text fontSize={['xs', 'sm']} color="black">
                      すでにアカウントをお持ちの方は
                      <NextLink href={loginUrl} passHref>
                        <Link
                          href={loginUrl}
                          title="すでにアカウントをお持ちの方はこちら"
                          onClick={() =>
                            sendAnalyticsEvent(
                              'induction',
                              'login',
                              'last_page',
                            )
                          }
                        >
                          こちら
                        </Link>
                      </NextLink>
                    </Text>
                  </Flex>
                </VStack>
              </Center>
            </SwiperSlide>
          )}
          {shouldShowRelatedSlidesPage && (
            <SwiperSlide
              data-hash="related_slides"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Center
                maxH={pageMaxHeight}
                maxW={['90%', pageMaxWidth]}
                height={pageMaxHeight}
                width={pageMaxWidth}
              >
                <ArticleRelatedSlidesPage
                  isFullscreen={isFullscreenActive}
                  articles={relatedArticles}
                />
              </Center>
            </SwiperSlide>
          )}
          {shouldShowLicenseVerifyPage && (
            <SwiperSlide
              data-hash="license_verify_required"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Center pos="relative">
                <SlideImage
                  h={isFullscreenActive ? '100%' : pageMaxHeight}
                  w={isFullscreenActive ? '100%' : pageMaxWidth}
                  src="/images/slide_require_license_verify.png"
                  alt="医師/医学生認証はこちら"
                />
                <VStack
                  pos="absolute"
                  bottom={['10%', '13%', '13%', '15.5%']}
                  spacing={[1, 2]}
                  w={pageMaxWidth * 0.86}
                  maxWidth={windowWidth * 0.8}
                  flex="1"
                >
                  <Button
                    size={loginButtonSize}
                    flexShrink={0}
                    colorScheme="brand"
                    flex="1"
                    px={[4, 10]}
                    py={[2, 3]}
                    onClick={async (e) => {
                      e.preventDefault();
                      // prettier-ignore
                      sendAnalyticsEvent('induction', 'doctor_certification', 'last_page');
                      await proceedIdPage(
                        `/users/doctor/register?from_service=slide`,
                      );
                    }}
                  >
                    <Text
                      color="white"
                      fontSize={['xs', 'md']}
                      fontWeight="bold"
                    >
                      医師/医学生認証を行う
                    </Text>
                  </Button>
                </VStack>
              </Center>
            </SwiperSlide>
          )}
        </Swiper>
        <Box pos="absolute" top={1} right={1} zIndex="docked">
          {isFullscreenEnabled && (
            <FullscreenButton
              activated={isFullscreenActive}
              onClick={onClickFullscreenButton}
            />
          )}
        </Box>
      </Center>
      <Flex
        w="100%"
        justify="space-between"
        align="center"
        bg="blackAlpha.800"
        px={[0, 1]}
        h={12}
      >
        <Text ml={2} textAlign="center" fontSize={['xs', 'sm']} color="white">
          <Text as="span">{activeIndex + 1}</Text>
          <Text as="span" mx={1}>
            /
          </Text>
          <Text as="span">{pageCount}</Text>
        </Text>
        {audioUrl && (
          <HStack
            overflow={'hidden'}
            position="relative"
            ml={2}
            display="flex"
            flexDirection="column"
            gap={0}
            color="white"
            borderBottom="blackAlpha.800"
          >
            <Text
              as="span"
              display="block"
              fontSize="xs"
              top={0}
              py={0.5}
              lineHeight={1}
            >
              音声解説
            </Text>
            <audio
              style={{
                width: audioWidth,
                height: '30px', // ChromeでもSafariでの最小サイズに合わせるため
              }}
              controls
              controlsList="nodownload"
              src={audioUrl}
              onPlay={() =>
                sendAnalyticsEvent('slide', 'audio_started', article.articleId)
              }
              onEnded={() => {
                sendAnalyticsEvent(
                  'slide',
                  'audio_finished',
                  article.articleId,
                );
              }}
            />
          </HStack>
        )}
        <HStack
          flex={1}
          justifyContent={'flex-end'}
          pl={[0, 2]}
          pr={2}
          h={10}
          spacing={0}
        >
          {shouldShowQuestionAndPageLikeButtons && (
            <>
              {activeIndex < article.pageCount &&
                !isDoctorRecruiterAccount &&
                author.antaaAccountType !== AntaaAccountType.CORPORATION &&
                !(
                  shouldShowLicenseVerifyPage &&
                  activeIndex === articlePages.length
                ) && (
                  <QuestionButton
                    activeIndex={activeIndex}
                    getArticleCommentCount={getArticleCommentCount}
                    fetchArticleComments={fetchArticleComments}
                    postArticleComment={postArticleComment}
                    achievedBadgeModalOnOpen={achievedBadgeModalOnOpen}
                    setBadge={setBadge}
                  />
                )}
              {!isDoctorRecruiterAccount && (
                <PageLikeOrUnlikeButton
                  liked={likes.pages[activeIndex]}
                  onClickLike={() => onClickPageLike(activeIndex + 1)}
                  onClickUnlike={() => onClickPageUnlike(activeIndex + 1)}
                />
              )}
              {!(
                author.antaaAccountType === AntaaAccountType.CORPORATION &&
                author.antaaAccountSubType ===
                  AntaaAccountSubType.PHARMACEUTICAL
              ) && <SlideSwiperMenu article={article} />}
            </>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

/**
 * {@link SlideSwiper}コンポーネントにいくつかのHACKを加えたコンポーネント
 *
 * HACKが散らばらないよう、1箇所にまとめる目的。
 */
function HackedSlideSwiper({
  article,
  author,
  likes,
  activeIndex,
  setActiveIndex,
  onClickPageLike,
  onClickPageUnlike,
  isDemoAccount,
  isPharmaceuticalAccount,
  isDoctorRecruiterAccount,
  relatedArticles,
  getArticleCommentCount,
  fetchArticleComments,
  postArticleComment,
  achievedBadgeModalOnOpen,
  setBadge,
}: {
  article: Article;
  author: Author;
  likes: Likes;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onClickPageLike: (pageNum: number) => void;
  onClickPageUnlike: (pageNum: number) => void;
  isDemoAccount: boolean;
  isPharmaceuticalAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  relatedArticles: ArticleHeader[];
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  postArticleComment: (
    message: string,
    discloseScope?: CommentDiscloseScope,
    pageNumber?: number,
  ) => Promise<Array<BadgeArgs>>;
  achievedBadgeModalOnOpen: () => void;
  setBadge: React.Dispatch<React.SetStateAction<BadgeArgs | undefined>>;
}) {
  const { isAuthenticated } = useAppContext();
  const router = useRouter();

  // HACK: ログイン済状態でページを開いた時に、URLのハッシュで指定されたページを開く。
  //
  // 問題が起きる仕組み:
  // 1. ページを開いた直後は非ログイン扱いであり、isAuthenticated=falseであるとしてrenderされる。
  //     - state更新までのタイムラグに起因する
  // 2. 公開範囲限定のスライドでは、isAuthenticated=falseである場合は3ページ目までしか表示しない。
  // 3. URLのハッシュが4ページ目以降を指し示していた場合は、1ページ目を表示してしまう。
  // 4. isAuthenticated=trueに切り替わっても、Swiperコンポーネントは初期化済みのため、1ページ目を表示したままになる。
  //
  // 解決策:
  // - isAuthenticated=trueに切り替わった時に、hashchangeイベントを発火する。
  //
  // 解決するメカニズム:
  // - Swiperは、hashchangeイベントが起きた場合にURLのハッシュで指定されたページを開く機能を持つ。
  //     - watchState=true時に限定
  //     - https://github.com/nolimits4web/swiper/blob/2dae84fa/src/modules/hash-navigation/hash-navigation.js#L66-L68
  useEffect(() => {
    if (isAuthenticated) {
      window.dispatchEvent(new Event('hashchange'));
    }
  }, [isAuthenticated]);

  // HACK: URLハッシュが変更されたことをNext.jsに伝える。
  //
  // 問題が起きる仕組み:
  // 1. スライド詳細画面を開く。
  //     - URLが /article/view/articleid になると仮定する。
  //     - Next.jsは /article/view/articleid に対応する画面を記憶する。
  // 2. スライドのページめくりをすると、Swiper.jsが内部でhistory.replaceStateを呼び出してURLハッシュを変更する。
  //     - URLが /article/view/articleid#2 になると仮定する。
  //     - Next.jsはURLハッシュ変更に気づかない。
  // 3. next/linkまたはnext/routerの機能で画面遷移をする。
  //     - URLが /mypage になると仮定する。
  // 4. ブラウザのバックボタンで戻る。
  //     - URLは /article/view/articleid#2 に戻る。
  //     - 表示されている画面は /mypage のもののままとなっている。★これが問題
  //     - Next.jsは /article/view/articleid#2 に対応する画面を知らないため、描画できない。
  //
  // 解決策:
  // - Swiper.jsによるURLハッシュ変更をNext.jsにも伝える。
  //
  // 注意点:
  // - router.replaceを呼び出す時は小さなディレイを入れる必要あり。以下のエラーが出てしまう。
  //     - Failed to execute 'measure' on 'Performance': The mark 'beforeRender' does not exist.
  const onHashSet = useCallback(
    (swiper: SwiperClass) => {
      setTimeout(() => {
        if (Number.isInteger(swiper.activeIndex)) {
          router.replace(`#${swiper.activeIndex + 1}`);
        }
      }, 100);
    },
    [router],
  );

  // HACK: スライド詳細画面間での遷移時にSwiper内部のactiveIndexをリセットする。
  //
  // 問題が起きる仕組み:
  // 1. スライド詳細画面を開く。
  //     - URLが /article/view/ABC になると仮定する。
  // 2. ページめくりをする。
  //     - URLが /article/view/ABC#2 になる。
  //     - Swiper内部のactiveIndexの値は2になる。
  // 3. スライド詳細画面内のリンクから他のスライド詳細画面を開く。
  //     - URLが /article/view/XYZ になると仮定する。
  //     - Swiper内部のactiveIndexの値は2のままである。★これが問題
  //     - スライド XYZ の2ページ目が表示されてしまう。
  //
  // 解決策:
  // - URLが切り替わった際にSwiperコンポーネントをマウントし直す。
  //     - 一度アンマウントし、再度マウントすることで実現する。
  //
  // 解決するメカニズム:
  // - articleIdをkeyに指定することで、articleIdが変化した際にコンポーネントが再生成され、stateも初期状態に戻る。
  //
  // 補足:
  // - 同一ページ間の遷移でstateがリセットされない問題は、Next.jsではよく知られている事象。
  //     - https://nextjs.org/docs/api-reference/next/router#resetting-state-after-navigation
  const componentKey = article.articleId;

  const swiperOptions = {
    onHashSet,
  };
  return (
    <SlideSwiper
      key={componentKey}
      article={article}
      author={author}
      swiperOptions={swiperOptions}
      likes={likes}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      onClickPageLike={onClickPageLike}
      onClickPageUnlike={onClickPageUnlike}
      isDemoAccount={isDemoAccount}
      isPharmaceuticalAccount={isPharmaceuticalAccount}
      isDoctorRecruiterAccount={isDoctorRecruiterAccount}
      relatedArticles={relatedArticles}
      getArticleCommentCount={getArticleCommentCount}
      fetchArticleComments={fetchArticleComments}
      postArticleComment={postArticleComment}
      achievedBadgeModalOnOpen={achievedBadgeModalOnOpen}
      setBadge={setBadge}
    />
  );
}

function SlideVideo({ article }: { article: Article }) {
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();

  if (article.discloseScope === 'PARTIAL' && !isAuthenticated) {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}#${article.pages.length}`,
    );
    const loginUrl = `/login?redirectUrl=${redirectUrl}`;
    const bgUrl = `/api/article/view-image/${article.articleId}/${article.topImageM}?v=${article.updateDateEpochMilliseconds}`;
    return (
      <NextLink href={loginUrl} passHref>
        <Link>
          <Box pos="relative">
            <Image alt={article.name || ''} src={bgUrl} w="full" />
            <Center pos="absolute" inset={0} bg="blackAlpha.700" p={4}>
              <Text color="white">動画を視聴するにはログインが必要です。</Text>
            </Center>
          </Box>
        </Link>
      </NextLink>
    );
  }

  if (
    article.discloseScope === 'PARTIAL' &&
    isAuthenticated &&
    !isLicenseVerified
  ) {
    const bgUrl = `/api/article/view-image/${article.articleId}/${article.topImageM}?v=${article.updateDateEpochMilliseconds}`;
    return (
      <button
        style={{ width: '100%' }}
        onClick={async () => {
          // prettier-ignore
          sendAnalyticsEvent('induction', 'doctor_certification', 'from_view_movie');
          await proceedIdPage(`/users/doctor/register?from_service=slide`);
        }}
      >
        <Box pos="relative">
          <Image alt={article.name || ''} src={bgUrl} w="full" />
          <Center pos="absolute" inset={0} bg="blackAlpha.700" p={4}>
            <Text color="white">
              動画を視聴するには医師/医学生認証が必要です。
            </Text>
          </Center>
        </Box>
      </button>
    );
  }

  const videoUrl = `/api/article/view-video/${article.articleId}/${
    article.video!.fileName
  }`;
  return (
    <video
      controls
      controlsList="nodownload"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        width: '100%',
      }}
    >
      <source src={videoUrl} />
    </video>
  );
}

function SlideViewer({
  article,
  author,
  likes,
  activeIndex,
  setActiveIndex,
  onClickPageLike,
  onClickPageUnlike,
  isDemoAccount,
  isPharmaceuticalAccount,
  isDoctorRecruiterAccount,
  relatedArticles,
  getArticleCommentCount,
  fetchArticleComments,
  postArticleComment,
  achievedBadgeModalOnOpen,
  setBadge,
}: {
  article: Article;
  author: Author;
  likes: Likes;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onClickPageLike: (pageNum: number) => void;
  onClickPageUnlike: (pageNum: number) => void;
  isDemoAccount: boolean;
  isPharmaceuticalAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  relatedArticles: ArticleHeader[];
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  postArticleComment: (
    message: string,
    discloseScope?: CommentDiscloseScope,
    pageNumber?: number,
  ) => Promise<Array<BadgeArgs>>;
  achievedBadgeModalOnOpen: () => void;
  setBadge: React.Dispatch<React.SetStateAction<BadgeArgs | undefined>>;
}) {
  if (article.type === 'VIDEO') {
    return <SlideVideo article={article} />;
  }
  return (
    <HackedSlideSwiper
      article={article}
      author={author}
      likes={likes}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      onClickPageLike={onClickPageLike}
      onClickPageUnlike={onClickPageUnlike}
      isDemoAccount={isDemoAccount}
      isPharmaceuticalAccount={isPharmaceuticalAccount}
      isDoctorRecruiterAccount={isDoctorRecruiterAccount}
      relatedArticles={relatedArticles}
      getArticleCommentCount={getArticleCommentCount}
      fetchArticleComments={fetchArticleComments}
      postArticleComment={postArticleComment}
      achievedBadgeModalOnOpen={achievedBadgeModalOnOpen}
      setBadge={setBadge}
    />
  );
}

/**
 * スライド上表示 Antaa Slide Award 2023「つながる力」賞アンケート導線
 */
/*
function UsefulFormLinkUpper({ article }: { article: Article }) {
  const formUrl =
    'https://docs.google.com/forms/d/e/1FAIpQLScSn6Xq_T7Zjnke02sL7yUB9HydEp81kwSgG8OGmJ8MX4zXvw/viewform?usp=sf_link';
  const onClick = useCallback(() => {
    sendAnalyticsEvent('link_to', 'form', 'from_feedback_banner_award_2023');
    window.open(formUrl, '_blank', 'noopener,noreferrer');
  }, [formUrl]);

  return (
    <HStack
      px={4}
      py={2}
      w="full"
      bg="awardGold"
      onClick={onClick}
      cursor="pointer"
    >
      <Text color="white">
        Antaa Slide Award
        2024開催中！あなたが2024年に一番お世話になったスライドを教えてください！
      </Text>
    </HStack>
  );
}*/

/**
 * スライド下表示 Antaa Slide Award 2023「つながる力」賞アンケート導線
 */
/*
function UsefulFormLink({
  article,
  author,
}: {
  article: Article;
  author: Author;
}) {
  const formUrl =
    'https://docs.google.com/forms/d/e/1FAIpQLScSn6Xq_T7Zjnke02sL7yUB9HydEp81kwSgG8OGmJ8MX4zXvw/viewform?usp=sf_link';
  const onClick = useCallback(() => {
    sendAnalyticsEvent('link_to', 'form', 'from_feedback_banner_award_2023');
    window.open(formUrl, '_blank', 'noopener,noreferrer');
  }, [formUrl]);

  const fullName =
    author.userName || `${author.lastName ?? ''}${author.firstName ?? ''}`;
  const buttonLabel = useBreakpointValue({
    base: '投稿者に感謝を伝える',
    md: `${fullName} 先生に感謝を伝える`,
  });

  return (
    <Button
      w="full"
      borderRadius="full"
      bg="awardGold"
      color="white"
      variant="outline"
      onClick={onClick}
    >
      {buttonLabel}
    </Button>
  );
}
  */

function RightAsideArticle({ article }: { article: ArticleHeader }) {
  const handleLinkClick = () => {
    sendAnalyticsEvent('slide', 'click', 'from_right_aside_related_article');
  };

  return (
    <HStack key={article.articleId} justifyContent="flex-start">
      <NextLink href={`/article/view/${article.articleId}`}>
        <Link
          w="120px"
          h="80px"
          backgroundColor="blackAlpha.300"
          position="relative"
          flexShrink={0}
          _hover={{ opacity: '0.8' }}
          onClick={handleLinkClick}
        >
          <NextImage
            src={`/api/article/view-image/${article.articleId}/${article.topImageM}`}
            alt={article.name || ''}
            layout="fill"
            objectFit="contain"
          />
        </Link>
      </NextLink>
      <VStack alignItems="stretch" flexGrow={1}>
        <NextLink href={`/article/view/${article.articleId}`}>
          <Link color="inherit" onClick={handleLinkClick}>
            <Heading fontSize="small" noOfLines={2} wordBreak="break-word">
              {article.name}
            </Heading>
          </Link>
        </NextLink>
        <HStack fontSize="x-small" gap={2} color="gray.400">
          <VStack justifyContent="flex-start" alignItems="flex-start" gap={1}>
            <Text noOfLines={1}>{createUserName(article.author)}</Text>
            <HStack>
              <HStack justifyContent="flex-start" gap={1}>
                <IoEyeOutline size={14} />
                <Text>{article.sessionCount}</Text>
              </HStack>
              <HStack gap={1}>
                <IoBookmark />
                <Text>{article.likeCount}</Text>
              </HStack>
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </HStack>
  );
}

function RightAside({
  relatedArticles: articles,
  special,
}: {
  relatedArticles: ArticleHeader[];
  special?: Special;
}) {
  const hasSpecial = !!special;
  const relatedArticlesCount = useBreakpointValue({ base: 4, '2xl': 7 }) || 4;
  const relatedArticlesCountWhenHasSpecial =
    useBreakpointValue({ base: 2, '2xl': 5 }) || 2;

  // タグ指定バナーの表示イベント計測（PC表示、SP表示どちらもここで計測）
  useEffect(() => {
    if (hasSpecial) {
      sendAnalyticsEvent('slide', 'show', `${special.name}_banner`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSpecial]);

  return (
    <VStack
      height="100%"
      width="100%"
      alignItems="stretch"
      justifyContent={
        hasSpecial && articles.length < relatedArticlesCountWhenHasSpecial
          ? 'flex-start'
          : 'space-between'
      }
    >
      {hasSpecial && (
        <Box
          textAlign="center"
          w="full"
          maxW="470" // 画面幅が大きくなるにつれバナーも大きくなり、関連するスライドがはみ出るため指定
          aspectRatio={1300 / 720} // 画像のアスペクト比を指定
          pos="relative"
        >
          <Link
            _hover={{ opacity: '0.8' }}
            onClick={() =>
              sendAnalyticsEvent('slide', 'click', `${special.name}_banner`)
            }
            isExternal={special.isExternalLink ?? false}
            href={special.link}
          >
            <NextImage
              src={special.detailBannerImage ?? special.mainImage}
              alt={special.alt}
              layout="fill"
              objectFit="contain"
            />
          </Link>
        </Box>
      )}
      <VStack alignItems="stretch" flexGrow={hasSpecial ? 0 : 1}>
        <Heading fontSize="md">関連するスライド</Heading>
        {hasSpecial ? (
          <VStack alignItems="stretch">
            {articles
              .slice(0, relatedArticlesCountWhenHasSpecial)
              .map((article) => (
                <RightAsideArticle key={article.articleId} article={article} />
              ))}
          </VStack>
        ) : (
          <VStack
            flexGrow={1}
            justifyContent={
              articles.length < relatedArticlesCount
                ? 'flex-start'
                : 'space-between'
            }
            alignItems="stretch"
          >
            {articles.slice(0, relatedArticlesCount).map((article) => (
              <RightAsideArticle key={article.articleId} article={article} />
            ))}
          </VStack>
        )}
      </VStack>
    </VStack>
  );
}

function AuthorProfile({
  author,
  awards,
}: {
  author: Author;
  awards: Award[];
}) {
  const isOfficial = author.isOfficialAccount ?? false;
  const awardYears = Array.from(
    new Set(awards.map((award) => award.year)),
  ).sort((a, b) => (a < b ? 1 : -1));
  awardYears.splice(1);

  return (
    <Flex align="center">
      <ProfileImage
        userId={author.userId}
        discloseId={author.discloseId}
        updateDateEpochMilliseconds={author.updateDateEpochMilliseconds}
        hasProfileImage={author.hasProfileImage}
        idBaseProfileUrl={author.idBaseProfileUrl}
        customSize={52}
      />
      <Box ms={2} maxW="100%" overflow="hidden">
        <Flex justifyContent={'flex-start'}>
          <NextLink
            href={`/profile/${author.discloseId || author.userId}`}
            passHref
          >
            <Link
              onClick={() => sendAnalyticsEvent('profile', 'click', 'profile')}
              fontSize="lg"
              fontWeight="bold"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {createUserName(author)}
            </Link>
          </NextLink>
          {awardYears.length > 0 && (
            <Box ml={2} h={6}>
              {awardYears.map((year) => (
                <Text
                  key={year}
                  as="span"
                  lineHeight={6}
                  fontSize={[8, 10]}
                  fontWeight="bold"
                  color="white"
                  bg="awardGold"
                  px={[1, 2]}
                  py={1}
                  borderRadius={3}
                  me={1}
                >
                  Award {year} 受賞者
                </Text>
              ))}
            </Box>
          )}
          {isOfficial && (
            <Box ml={1} mt={1}>
              <HiCheckBadge size={20} color="#008e14" />
            </Box>
          )}
        </Flex>
        <Text fontSize={12}>{author.hospital}</Text>
      </Box>
    </Flex>
  );
}

function ArticleDescription({
  article,
  targetReaderTypes,
}: {
  article: Article;
  targetReaderTypes: TargetReaderType[];
}) {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Box>
      <Collapse startingHeight={120} in={isOpen}>
        <VStack align="stretch" p={3}>
          {!!article.highlight && (
            <Box>
              <Heading fontSize="md">投稿した先生からのメッセージ</Heading>
              <Box mt={2}>
                {article.highlight.split(/\r?\n/).map((line, i) => (
                  <LinkifyText
                    key={i}
                    gaEventLabelPrefix={`article-highlight`}
                    isExternal={!article.needLogin}
                  >
                    {line}
                  </LinkifyText>
                ))}
              </Box>
            </Box>
          )}

          <Box>
            <Heading fontSize="md">概要</Heading>
            <Box mt={2}>
              {article.description?.split(/\r?\n/).map((line, i) => (
                <LinkifyText
                  key={i}
                  gaEventLabelPrefix={`article-description`}
                  isExternal={!article.needLogin}
                >
                  {line}
                </LinkifyText>
              ))}
            </Box>
          </Box>

          {targetReaderTypes.length > 0 && (
            <Box>
              <Heading fontSize="md">本スライドの対象者</Heading>
              <Box mt={2}>
                {targetReaderTypes?.map((t) => `${t.name}`).join('／')}
              </Box>
            </Box>
          )}

          {article.references.length > 0 && (
            <Box>
              <Heading fontSize="md">参考文献</Heading>
              <UnorderedList mt={2}>
                {article.references.map((reference, i) => (
                  <ListItem key={i}>
                    <LinkifyText
                      gaEventLabelPrefix={`article-references`}
                      isExternal={!article.needLogin}
                    >
                      {reference}
                    </LinkifyText>
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}
        </VStack>
      </Collapse>
      <Button
        variant="ghost"
        rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        fontSize="xs"
        textColor={'green'}
        w="100%"
        textAlign={'center'}
        onClick={onToggle}
      >
        {isOpen ? '閉じる' : 'もっと見る'}
      </Button>
    </Box>
  );
}

function RelatedArticles({
  articles,
  isMobile,
}: {
  articles: ArticleHeader[];
  isMobile: boolean;
}) {
  return (
    <>
      <SectionHeading mb={2}>関連するスライド</SectionHeading>
      <ArticlesGrid
        articles={articles}
        isMobile={isMobile}
        gaEventLabel={'from_slide_detail_same_tag'}
      />
    </>
  );
}

function SameDepartmentArticles({
  articles,
  isMobile,
}: {
  articles: ArticleHeader[];
  isMobile: boolean;
}) {
  return (
    <>
      <SectionHeading mb={2}>このスライドと同じ診療科のスライド</SectionHeading>
      <ArticlesGrid
        articles={articles}
        isMobile={isMobile}
        gaEventLabel={'same_department_from_slide_detail'}
      />
    </>
  );
}

function AuthorInterviews({
  author,
  interviews,
}: {
  author: Author;
  interviews: Interview[];
}) {
  const fullName = createUserName(author);
  return (
    <>
      <SectionHeading mb={2}>{`${fullName}さんのインタビュー`}</SectionHeading>
      <SimpleGrid w="full" columns={[1, null, 2]} gap={2}>
        {interviews.map((interview) => (
          <Box key={interview.id} shadow="sm">
            <Link
              href={interview.siteUrl}
              isExternal
              onClick={() => {
                sendAnalyticsEvent(
                  'link_to',
                  'interview',
                  `${interview.id}_from_slide_detail`,
                );
              }}
            >
              <Image
                src={interview.imageUrl}
                alt={`${fullName}さんのインタビュー`}
              />
            </Link>
          </Box>
        ))}
      </SimpleGrid>
    </>
  );
}

function AuthorOtherArticles({
  articles,
  author,
  isMobile,
}: {
  articles: ArticleHeader[];
  author: Author;
  isMobile: boolean;
}) {
  if (articles.length === 0) {
    return null;
  }
  return (
    <>
      <SectionHeading mb={2}>
        {createUserName(author)}
        さんの他の投稿スライド
      </SectionHeading>
      <ArticlesGrid
        articles={articles}
        isMobile={isMobile}
        gaEventLabel={'from_slide_detail_same_author'}
      />
    </>
  );
}

function FacebookShare({
  article,
  children,
}: {
  article: Article;
  children: ReactNode;
}) {
  const articleUrl = createArticleUrl(article.articleId);
  const shareUrl = createFacebookShareUrl({ url: articleUrl });
  return (
    <ExternalLink href={shareUrl} withIcon={false}>
      {children}
    </ExternalLink>
  );
}

function TwitterShare({
  author,
  article,
  children,
}: {
  author: Author;
  article: Article;
  children: ReactNode;
}) {
  const text = `${article.name}（${createUserName(author)}先生${
    author.twitterAccount ? '：@' + author.twitterAccount : ''
  }）| Antaa Slide @antaa__jp #Antaa`;
  const articleUrl = createArticleUrl(article.articleId);
  const shareUrl = createTwitterShareUrl({ text, url: articleUrl });
  return (
    <ExternalLink href={shareUrl} withIcon={false}>
      {children}
    </ExternalLink>
  );
}

function FacebookShareCircleButton({
  article,
  gaEventLabel,
}: {
  article: Article;
  gaEventLabel: string;
}) {
  const fontColor = useColorModeValue('blackAlpha.900', 'whiteAlpha.900');
  return (
    <FacebookShare article={article}>
      <VStack align="center" spacing={0}>
        <Tooltip label="シェア" placement="bottom" closeDelay={200}>
          <IconButton
            aria-label="Facebookにシェア"
            size="md"
            rounded="full"
            backgroundColor={'#fff'}
            icon={<MdFacebook size={28} color="#0866FF" />}
            onClick={() => sendAnalyticsEvent('slide', 'share', gaEventLabel)}
          />
        </Tooltip>
      </VStack>
    </FacebookShare>
  );
}

function TwitterShareCircleButton({
  author,
  article,
  gaEventLabel,
}: {
  author: Author;
  article: Article;
  gaEventLabel: string;
}) {
  const fontColor = useColorModeValue('blackAlpha.900', 'whiteAlpha.900');
  return (
    <TwitterShare author={author} article={article}>
      <VStack align="center" spacing={0}>
        <Tooltip label="ツイート" placement="bottom" closeDelay={200}>
          <IconButton
            aria-label="Twitterにシェア"
            size="md"
            rounded="full"
            backgroundColor="#fff"
            icon={<FaXTwitter size={24} color={'black'} />}
            onClick={() => sendAnalyticsEvent('slide', 'share', gaEventLabel)}
          />
        </Tooltip>
      </VStack>
    </TwitterShare>
  );
}

function ClipboardShareCircleButton({
  article,
  gaEventLabel,
}: {
  article: Article;
  gaEventLabel: string;
}) {
  const articleUrl = createArticleUrl(article.articleId);
  const { hasCopied, onCopy: copyToClipboard } = useClipboard(articleUrl);
  const fontColor = useColorModeValue('blackAlpha.900', 'whiteAlpha.900');
  return (
    <Popover isOpen={hasCopied} placement="top" autoFocus={false}>
      <PopoverTrigger>
        <VStack
          align="center"
          spacing={0}
          role="button"
          aria-label="コピー"
          onClick={() => {
            sendAnalyticsEvent('slide', 'share', gaEventLabel);
            copyToClipboard();
          }}
          _hover={{
            textDecoration: 'underline',
            textDecorationColor: 'brand.500',
          }}
        >
          <Tooltip label="コピー" placement="bottom" closeDelay={200}>
            <Box
              borderRadius="50%"
              padding={2}
              _hover={{
                bg: 'gray.200',
              }}
            >
              <MdContentCopy size={24} />
            </Box>
          </Tooltip>
        </VStack>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody>コピーしました</PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

/**
 * 保存完了時に表示されるモーダル。SNS等でのシェアに誘導する。
 */
function LikeModal({
  author,
  article,
  onClose,
  isOpen,
}: {
  author: Author;
  article: Article;
  onClose: () => void;
  isOpen: boolean;
}) {
  const imageUrl =
    createArticleImageRelativeUrl(article.articleId, article.topImageM, {
      version: article.updateDateEpochMilliseconds,
    }) || undefined;
  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>保存が完了しました！</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <HStack spacing={2}>
              {!(
                author.antaaAccountType === AntaaAccountType.CORPORATION &&
                author.antaaAccountSubType ===
                  AntaaAccountSubType.PHARMACEUTICAL
              ) && (
                <>
                  <FacebookShareCircleButton
                    article={article}
                    gaEventLabel={'facebook_after_stock'}
                  />
                  <TwitterShareCircleButton
                    author={author}
                    article={article}
                    gaEventLabel={'twitter_after_stock'}
                  />
                </>
              )}
              <ClipboardShareCircleButton
                article={article}
                gaEventLabel={'copy_url_after_stock'}
              />
            </HStack>
            <NextLink href="/mypage/like" passHref>
              <Button
                as="a"
                w="full"
                leftIcon={<IoBookmark />}
                rightIcon={<ChevronRightIcon />}
                variant="outline"
                size="sm"
                colorScheme="brand"
                onClick={() =>
                  sendAnalyticsEvent('slide', 'click', 'stock_after_stock')
                }
              >
                保存したスライドの一覧を見る
              </Button>
            </NextLink>
          </VStack>
          <Divider mt={4} />
          <Image src={imageUrl} alt={article.name || ''} mt={4} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function ArticleLikeOrUnlikeButton({
  author,
  article,
  liked,
  onClickLike,
  onClickUnlike,
  disclosure,
  style,
  gaEventLabel = null,
}: {
  author: Author;
  article: Article;
  liked: boolean;
  onClickLike: () => void;
  onClickUnlike: () => void;
  disclosure: LikeModalDisclosure;
  style?: {} | undefined;
  gaEventLabel?: string | null;
}) {
  return (
    <>
      {liked ? (
        <Button
          size="sm"
          flexShrink={0}
          leftIcon={<MdBookmarkAdded size="24px" />}
          onClick={() => {
            onClickUnlike();
            sendAnalyticsEvent('slide', 'unstock', gaEventLabel);
          }}
          {...style}
        >
          スライド保存済
        </Button>
      ) : (
        <Button
          size="sm"
          flexShrink={0}
          leftIcon={<MdOutlineBookmarkAdd size="24px" />}
          colorScheme="brand"
          onClick={() => {
            onClickLike();
            sendAnalyticsEvent('slide', 'stock', gaEventLabel);
          }}
          {...style}
        >
          スライド保存
        </Button>
      )}

      <LikeModal
        author={author}
        article={article}
        onClose={disclosure.onClose}
        isOpen={disclosure.isOpen}
      />
    </>
  );
}

function QuestionButton({
  activeIndex,
  postArticleComment,
  getArticleCommentCount,
  fetchArticleComments,
  achievedBadgeModalOnOpen,
  setBadge,
}: {
  activeIndex: number;
  postArticleComment: (
    message: string,
    discloseScope?: CommentDiscloseScope,
    pageNumber?: number,
  ) => Promise<Array<BadgeArgs>>;
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  achievedBadgeModalOnOpen: () => void;
  setBadge: React.Dispatch<React.SetStateAction<BadgeArgs | undefined>>;
}) {
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [canPostComment, setCanPostComment] = useState(false);

  const PopoverContentRef = useRef<HTMLElement>(null);
  const PopoverTriggerRef = useRef<HTMLButtonElement>(null);
  useOutsideClick({
    ref: PopoverContentRef,
    handler: (e: Event) => {
      // PopoverTriggerがクリックされた場合のみuseOutsideClickでは何もせず、PopoverTriggerのデフォルトの挙動を発火させる
      // 両方発火させると、PopoverTriggerのクリックでPopoverContentが閉じなくなる
      if (PopoverTriggerRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    },
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();

  const router = useAppRouter();
  const { articleId } = router.query;

  const handleLoginClick = () => {
    if (typeof articleId !== 'string') return;
    router.pushLogin({
      redirectUrl: `/article/view/${articleId}#${activeIndex + 1}`,
    });
  };

  const doctorRegister = async () => {
    // prettier-ignore
    sendAnalyticsEvent('induction', 'doctor_certification', 'from_question_input');
    await proceedIdPage(`/users/doctor/register?from_service=slide`);
  };

  const handleOpen = () => {
    if (!isAuthenticated) {
      toast({
        title: (
          <Text fontSize={{ base: 11, md: 14 }}>
            質問するには
            <Link onClick={handleLoginClick}>ログイン</Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }
    if (isAuthenticated && !isLicenseVerified) {
      toast({
        title: (
          <Text fontSize={{ base: 11, md: 14 }}>
            質問するには
            <Link onClick={doctorRegister}>医師/医学生認証</Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInputChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setMessage(e.target.value);
    setCanPostComment(e.target.value.trim().length > 0);
  };

  const postComment = () => {
    postArticleComment(
      message,
      isPublic ? commentDiscloseScope.PUBLIC : commentDiscloseScope.PRIVATE,
      activeIndex + 1,
    )
      .then((grantedBadges) => {
        setCanPostComment(false);
        setIsOpen(false);
        setMessage('');
        setIsPublic(true);
        toast({
          title:
            'コメントが送信されました。スライド投稿者からのご返信をお待ちください。',
          status: 'success',
          isClosable: true,
          variant: 'subtle',
        });
        getArticleCommentCount();
        fetchArticleComments();
        if (grantedBadges.length > 0) {
          setBadge(grantedBadges[grantedBadges.length - 1]);
          sendAnalyticsEvent('badge', 'achieved', 'comment');
          achievedBadgeModalOnOpen();
        }
      })
      .catch(() => {
        toast({
          title: 'コメントの送信に失敗しました。',
          description: 'しばらく待って再度お試しください。',
          status: 'error',
          isClosable: true,
          variant: 'subtle',
        });
      });
  };

  return (
    <Popover
      placement="top"
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      initialFocusRef={textareaRef}
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <IconButton
          ref={PopoverTriggerRef}
          w={[10, 16]}
          icon={
            <VStack spacing={0}>
              <MdOutlineChat size={16} />
              <Text fontSize={['xx-small', 'xs']}>質問</Text>
            </VStack>
          }
          variant="unstyled"
          color="white"
          _hover={{
            bg: 'whiteAlpha.300',
          }}
          _focus={{
            boxShadow: 'none',
          }}
          aria-label={'質問'}
        />
      </PopoverTrigger>
      <PopoverContent
        ref={PopoverContentRef}
        borderColor="brand.500"
        marginX={4}
      >
        <PopoverBody p={2}>
          <Textarea
            ref={textareaRef}
            h={160}
            maxWidth="full"
            p={0}
            border="none"
            resize="none"
            focusBorderColor="transparent"
            value={message}
            onChange={handleInputChange}
            placeholder="大変勉強になりました。お忙しいところ恐縮ですが、このページに記載してありますXXXについて伺いたい事があります。XXXの疾患の場合、薬剤の第一選択はXXXになりますでしょうか？ お時間あります時に、ご教示頂けますと幸いです。"
          />
        </PopoverBody>
        <PopoverFooter py={1} px={2}>
          <HStack justify="space-between" align="center">
            <Button
              onClick={postComment}
              colorScheme="brand"
              size="sm"
              borderRadius="full"
              isDisabled={!canPostComment}
            >
              投稿者に質問を送信
            </Button>
            <HStack align="center">
              <Text as="span">{isPublic ? '公開' : '非公開'}</Text>
              <Tooltip
                fontSize="xs"
                placement="top"
                label={
                  isPublic ? (
                    <Text>質問内容は、本スライドページに表示されます。</Text>
                  ) : (
                    <Text>
                      質問内容は、スライド投稿者のみに表示され、
                      <br />
                      他のユーザーには表示されません。
                    </Text>
                  )
                }
              >
                <QuestionOutlineIcon w={3.5} h={3.5} color="gray" />
              </Tooltip>
              <Switch
                defaultChecked
                size="md"
                colorScheme="brand"
                isChecked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
            </HStack>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}

/**
 * ページ保存/保存解除ボタン
 */
function PageLikeOrUnlikeButton({
  liked,
  onClickLike,
  onClickUnlike,
}: {
  liked: boolean;
  onClickLike: () => void;
  onClickUnlike: () => void;
}) {
  const label = 'ページ保存';

  const onClick = () => {
    if (liked) {
      onClickUnlike();
      sendAnalyticsEvent('page', 'unstock', null);
    } else {
      onClickLike();
      sendAnalyticsEvent('page', 'stock', null);
    }
  };

  return (
    <IconButton
      w={[14, 20]}
      icon={
        <>
          <VStack spacing={0}>
            {liked ? <MdBookmarkAdded /> : <MdOutlineBookmarkAdd />}
            <Text fontSize={['xx-small', 'xs']}>{label}</Text>
          </VStack>
        </>
      }
      variant="unstyled"
      color="white"
      _hover={{
        bg: 'whiteAlpha.300',
      }}
      _focus={{
        boxShadow: 'none',
      }}
      onClick={onClick}
      aria-label={label}
    />
  );
}

function EmbedModal({
  article,
  isOpen,
  onClose,
}: {
  article: Article;
  isOpen: boolean;
  onClose: () => void;
}) {
  const previewUrl = `/article/embed/${article.articleId}?preview=true`;
  const previewSize = useMemo(() => {
    const width = 480;
    let height = Math.floor((width * 9) / 16);
    if (article.height && article.width) {
      height = Math.floor((width * article.height) / article.width);
    }
    return { width, height };
  }, [article]);

  type DeviceType = 'PC' | 'SP';
  const [embedDevice, setEmbedDevice] = useState<DeviceType>('SP');
  const onChangeEmbedDevice = useCallback<
    ChangeEventHandler<HTMLSelectElement>
  >(
    (e) => {
      if (['PC', 'SP'].includes(e.target.value)) {
        setEmbedDevice(e.target.value as DeviceType);
      }
    },
    [setEmbedDevice],
  );

  const embedSize = useMemo(() => {
    const width = embedDevice === 'PC' ? 960 : 360;
    let height = Math.floor((width * 9) / 16);
    if (article.height && article.width) {
      height = Math.floor((width * article.height) / article.width);
    }
    return { width, height };
  }, [article, embedDevice]);

  const [embedPageNo, setEmbedPageNo] = useState(1);
  const embedPageNoList = [
    ...Array(
      article.discloseScope === 'FULL'
        ? article.pageCount
        : ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT,
    ),
  ].map((_, i) => i + 1);
  const onChangeEmbedPageNo = useCallback<
    ChangeEventHandler<HTMLSelectElement>
  >(
    (e) => {
      setEmbedPageNo(Number(e.target.value));
    },
    [setEmbedPageNo],
  );

  const embedHtml = useMemo(() => {
    const embedUrl =
      article.type === 'VIDEO'
        ? `${createArticleEmbedUrl(article.articleId)}`
        : `${createArticleEmbedUrl(article.articleId)}#${embedPageNo}`;
    return `<iframe src="${embedUrl}" width="${embedSize.width}" height="${embedSize.height}" frameborder="0" marginwidth="0" marginheight="0" scrolling="no" style="border:1px solid #CCC; border-width:1px; margin-bottom:5px; max-width: 100%;" allowfullscreen></iframe>`;
  }, [article, embedSize, embedPageNo]);

  const { onCopy } = useClipboard(embedHtml);
  const {
    isOpen: isOpenTooltip,
    onOpen: onOpenTooltip,
    onClose: onCloseTooltip,
  } = useDisclosure();
  const onClickCopy = () => {
    onCopy();
    onOpenTooltip();
  };

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          あなたのWebサイトやブログにスライドを貼り付けられます
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex>
            <Box flex={3}>
              <iframe
                src={previewUrl}
                width={previewSize.width}
                height={previewSize.height}
                frameBorder="0"
                marginWidth={0}
                marginHeight={0}
                scrolling="no"
                style={{
                  border: '1px solid #ccc',
                  borderWidth: '1px',
                  marginBottom: '5px',
                  maxWidth: '100%',
                }}
                allowFullScreen
              ></iframe>
            </Box>
            <VStack flex={2} align="flex-start" spacing={4} ps={2}>
              <FormControl>
                <FormLabel>対象端末</FormLabel>
                <Select value={embedDevice} onChange={onChangeEmbedDevice}>
                  <option value="PC">PC向け</option>
                  <option value="SP">スマホ向け</option>
                </Select>
              </FormControl>
              {article.type === 'SLIDE' && (
                <FormControl>
                  <FormLabel>表示開始ページ</FormLabel>
                  <Select value={embedPageNo} onChange={onChangeEmbedPageNo}>
                    {embedPageNoList.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Divider />
              <Box>
                <Tooltip
                  label="コピーしました"
                  placement="right"
                  isOpen={isOpenTooltip}
                  onClose={onCloseTooltip}
                  closeDelay={2000}
                >
                  <Button variant="outline" onClick={onClickCopy}>
                    コピーする
                  </Button>
                </Tooltip>
                <Code wordBreak="break-all" fontSize="xs" p={2} mt={1}>
                  {embedHtml}
                </Code>
              </Box>
            </VStack>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function SlideTextLoginInductionButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      colorScheme="brand"
      fontSize={['sm', 'md', 12, 'sm']}
      size="md"
      flexShrink={0}
      w="100%"
      textAlign={'center'}
      onClick={onClick}
      cursor="pointer"
    >
      テキスト全文を見る
    </Button>
  );
}

// 目次のObjectを繰り返し処理で使いやすい形に整形する
// { "目次範囲開始ページ番号": "目次内容" }
function generateDisplayTocMap(
  tableOfContents: TableOfContents[] | null,
): { [key: string]: string } | null {
  if (!tableOfContents) return null;

  return Object.fromEntries(
    tableOfContents.map((t) => [t.pageRange.split('-')[0], t.contents]),
  );
}

// 対象ページ番号が目次範囲の開始位置に該当する場合、目次をh2で表示する
function Toc({ contents }: { contents: string }) {
  return (
    <Heading as="h2" fontSize="md" mt={4} color="black" overflow="hidden">
      {contents}
    </Heading>
  );
}

function SlideTextContent({ article }: { article: Article }) {
  const { isAuthenticated, isLicenseVerified, user } = useAppContext();

  // ログイン済みで医師/医学生認証済みか[公開設定]で[全員]としたスライドの場合のみ全ページ表示する
  const pages =
    (isAuthenticated && isLicenseVerified) || article.discloseScope === 'FULL'
      ? article.pages
      : article.pages.slice(0, ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT);

  const displayTocMap: { [key: string]: string } | null = generateDisplayTocMap(
    article.tableOfContents,
  );

  return (
    <>
      <Text fontSize="sm" fontWeight="bold">
        テキスト全文
      </Text>
      {/* 投稿者本人かつ目次がある場合に表示 */}
      {isAuthenticated &&
        article.authorId === user?.id &&
        article.tableOfContents && (
          <Link href={URL_INQUIRY_FORM_MULTI} isExternal width="fit-content">
            目次に関するお問い合わせはこちら
          </Link>
        )}
      {pages.map((page) => (
        <React.Fragment key={page.pageNumber}>
          {displayTocMap && displayTocMap[page.pageNumber] && (
            <Toc contents={displayTocMap[page.pageNumber]}></Toc>
          )}
          <HStack color="gray" fontSize="sm" align="flex-start">
            <Link
              href={`#${page.pageNumber}`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              {`#${page.pageNumber}. `}
            </Link>
            <VStack align="flex-start" spacing={0}>
              {page.slideText ? (
                page.slideText.split(/\r?\n/).map((line, i) => (
                  <LinkifyText
                    key={i}
                    gaEventLabelPrefix={`article-text`}
                    isExternal={!article.needLogin}
                    as="h3"
                  >
                    {line}
                  </LinkifyText>
                ))
              ) : (
                <Text as="h3">-</Text>
              )}
            </VStack>
          </HStack>
        </React.Fragment>
      ))}
      {/* SEOのため、鍵付きスライドの場合、未ログインユーザーには4ページ目以降は目次のみ表示する */}
      {pages.length < article.pages.length &&
        article.pages
          .slice(ARTICLE_PARTIAL_DISCLOSE_PAGE_COUNT)
          .map((page2) => (
            <React.Fragment key={page2.pageNumber}>
              {displayTocMap && displayTocMap[page2.pageNumber] && (
                <Toc contents={displayTocMap[page2.pageNumber]}></Toc>
              )}
            </React.Fragment>
          ))}
    </>
  );
}

function SlideTexts({
  article,
  activeIndex,
}: {
  article: Article;
  activeIndex: number;
}) {
  const router = useAppRouter();
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const { isOpen, onToggle } = useDisclosure();
  const borderColor = useColorModeValue('gray.200', 'gray.500');
  const borderWidth = useColorModeValue(0, 1);

  const startingHeightUnauth = useBreakpointValue([125, 170]);

  const handlePushLogin = () => {
    sendAnalyticsEvent('induction', 'login', 'slide_text');

    router.pushLogin({
      redirectUrl: `/article/view/${article.articleId}#${activeIndex + 1}`,
    });
  };

  const doctorRegister = async () => {
    // prettier-ignore
    sendAnalyticsEvent('induction', 'doctor_certification', 'from_slide_text');
    await proceedIdPage(`/users/doctor/register?from_service=slide`);
  };

  if (!article.pages) {
    return null;
  }

  return (
    <Box
      borderColor={'blackAlpha.300'}
      borderWidth={1}
      borderRadius="lg"
      width="100%"
    >
      <Box
        borderColor={borderColor}
        borderWidth={borderWidth}
        borderRadius="lg"
      >
        <Collapse
          startingHeight={
            isAuthenticated && isLicenseVerified ? 120 : startingHeightUnauth
          }
          in={isOpen}
        >
          <VStack align="stretch" p={3}>
            <SlideTextContent article={article} />
          </VStack>
        </Collapse>
        <Button
          variant="ghost"
          rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          fontSize="xs"
          textColor={'green'}
          w="100%"
          textAlign={'center'}
          onClick={onToggle}
        >
          {isOpen ? '閉じる' : 'もっと見る'}
        </Button>
        {!isAuthenticated && article.discloseScope !== 'FULL' && (
          <Box w="100%" p={3} mt={2} mb={1}>
            <VStack gap={2} w="100%">
              <Text
                fontSize={['xs', 'sm', 10, 'xs']}
                fontWeight="bold"
                textAlign="center"
              >
                会員登録・ログインで「スライド内のテキスト」を参照いただけます。
              </Text>
              <Box w="100%">
                <SlideTextLoginInductionButton onClick={handlePushLogin} />
              </Box>
            </VStack>
          </Box>
        )}
        {isAuthenticated &&
          !isLicenseVerified &&
          article.discloseScope !== 'FULL' && (
            <Box w="100%" p={3} mt={2} mb={1}>
              <VStack gap={2} w="100%">
                <Text
                  fontSize={['xs', 'sm', 10, 'xs']}
                  fontWeight="bold"
                  textAlign="center"
                >
                  医師/医学生認証で「スライド内のテキスト」を参照いただけます。
                </Text>
                <Box w="100%">
                  <SlideTextLoginInductionButton onClick={doctorRegister} />
                </Box>
              </VStack>
            </Box>
          )}
      </Box>
    </Box>
  );
}

function SignupLeadForPartialDisclose({ article }: { article: Article }) {
  const { idBaseUrl } = getPublicRuntimeConfig();
  const accountSignupUrl = `${idBaseUrl}/signup?from_service=slide`;
  const redirectUrl = encodeURIComponent(`/article/view/${article.articleId}`);
  const signupUrl = `${accountSignupUrl}&redirect_url=${redirectUrl}`;

  return (
    <Text textAlign="center" mb={4}>
      このスライドは会員限定コンテンツです。
      <Box display={['inline', 'inline', 'none']} as="span">
        <br />
      </Box>
      今すぐ
      <NextLink href={signupUrl} passHref>
        {/* アカウント登録は事前のID基盤ログインが不要なためproceedIdPageを使わない */}
        <Link sx={{ textDecoration: 'underline' }}>アカウント登録（無料）</Link>
      </NextLink>
      して、続きを読みましょう！
    </Text>
  );
}

function HiddenNotice({
  article,
  isAdminAccount,
  isAuthor,
}: {
  article: Article;
  isAdminAccount: boolean;
  isAuthor: boolean;
}) {
  const linkUrl = isAdminAccount
    ? `/manager/articles/${article.articleId}/edit`
    : `/article/edit/${article.articleId}`;
  const articleUrl = createArticleUrl(article.articleId);
  const urlCopyButtonLabel = useBreakpointValue([
    <></>,
    <></>,
    // eslint-disable-next-line react/jsx-key
    <Text fontSize="sm" ml={2}>
      限定公開URL取得
    </Text>,
  ]);
  const urlCopyButtonSize = useBreakpointValue([26, 18]);
  const { apiClient } = useApiClient();
  const [limitedAccessUrl, setLimitedAccessUrl] = useState('');
  const { hasCopied, onCopy: copyToClipboard } = useClipboard(limitedAccessUrl);

  const getAccessKey = async () => {
    try {
      const res = await apiClient(
        `/api/article/${article.articleId}/limited-share`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (res.ok) {
        const data = await res.json();
        await setLimitedAccessUrl(`${articleUrl}?key=${data.accessKey}`);
      } else {
        throw new Error();
      }
    } catch (e) {
      await setLimitedAccessUrl('URLの取得に失敗しました');
    }
  };

  return (
    <>
      {(isAuthor || isAdminAccount) && (
        <Alert status="warning" mt={4}>
          <Text>
            このスライドは非公開となっています。 公開するには
            <NextLink href={linkUrl} passHref>
              <Link>こちら</Link>
            </NextLink>
            から [スライド公開設定] を変更して下さい。
          </Text>
        </Alert>
      )}
      {!isAuthor && (
        <Alert status="warning" mt={4}>
          <Text>このスライドは限定公開となっています。</Text>
        </Alert>
      )}
      {isAuthor && (
        <>
          <Alert status="info">
            <IconButton
              aria-label="限定公開URL取得"
              colorScheme="gray"
              mr={4}
              pl={4}
              pr={4}
              icon={
                <>
                  <FaLink size={urlCopyButtonSize} />
                  {urlCopyButtonLabel}
                </>
              }
              onClick={() => {
                sendAnalyticsEvent('slide', 'share', 'get_limited_share_url');
                getAccessKey();
              }}
            />
            <Text>
              このボタンをクリックすると限定公開用URLが生成されます。
              <br />
              注）クリックする度に新しいURLが取得され、以前取得したURLは無効となります。
            </Text>
          </Alert>
          {!!limitedAccessUrl && (
            <Alert status="info">
              <Text fontSize={'sm'}>{`[URL] ⇒ ${limitedAccessUrl}`}</Text>
              {/* URL払い出しボタンクリックの延長でクリップボードへのコピー（onCopy）を実行すると、
                  Chakra UIの仕様（不備）により更新前の値がコピーされてしまう。
                  対応策として、払い出しとコピーを別ボタンに分けて実装した。 */}
              <IconButton
                aria-label="URLコピー"
                colorScheme="gray"
                size={'sm'}
                ml={2}
                pl={4}
                pr={4}
                icon={<Text>コピー</Text>}
                onClick={() => {
                  sendAnalyticsEvent(
                    'slide',
                    'share',
                    'copy_limited_share_url',
                  );
                  copyToClipboard();
                }}
              />
            </Alert>
          )}
          <Popover isOpen={hasCopied} placement="bottom-end" autoFocus={false}>
            <PopoverTrigger>
              <Box w={0} />
            </PopoverTrigger>
            <PopoverContent>
              <PopoverBody>コピーしました</PopoverBody>
            </PopoverContent>
          </Popover>
        </>
      )}
    </>
  );
}

function nonPublicMessage() {
  return (
    <Box mt={30}>
      <Center>
        <VStack>
          <Text mb={8} fontSize="lg">
            このスライドは投稿者によって非公開になっております。
          </Text>
          <NextLink href="/" passHref>
            <Link>トップページへ</Link>
          </NextLink>
        </VStack>
      </Center>
    </Box>
  );
}

function MobileBanner({
  image,
  alt,
  link,
  name,
  width = 1200,
  height = 240,
}: {
  image: string;
  alt: string;
  link: string;
  name: string;
  width?: number;
  height?: number;
}) {
  return (
    <NextLink href={link} passHref>
      <Link
        _hover={{ opacity: '0.8' }}
        onClick={() => sendAnalyticsEvent('slide', 'click', `${name}_banner`)}
      >
        <NextImage
          src={image}
          alt={alt}
          width={width}
          height={height}
          style={{ width: '100%', aspectRatio: width / height }}
        />
      </Link>
    </NextLink>
  );
}

function MobileSpecialBanner({ special }: { special: SpecialHasMobileBanner }) {
  const { name, alt, link, mobileBannerImage } = special;

  return (
    <Box mt={8}>
      <MobileBanner
        link={link}
        name={name}
        image={mobileBannerImage}
        alt={alt}
      />
    </Box>
  );
}

function MobileSpecialBar({
  special: { key, alt, link, image, width, height },
}: {
  special: MobileSpecialBar;
}) {
  return (
    <Box position="fixed" bottom={0} zIndex="900">
      <Box rounded={['lg', 'xl', null]}>
        <MobileBanner
          link={link}
          name={key}
          image={image}
          alt={alt}
          width={width}
          height={height}
        />
      </Box>
    </Box>
  );
}

const ArticleDetailPage: NextPage<PageContext> = ({
  article,
  tags,
  author,
  authorAwards,
  authorInterviews,
  authorOtherArticles,
  hasMoreAuthorOtherArticles,
  sameTagArticles,
  sameDepartmentArticles,
  likes: initialLikes,
  articleDepartments,
  allDepartments,
  targetReaderTypes,
  richResultArticle,
  isMobile,
  isAdminAccount,
  isDemoAccount,
  isPharmaceuticalAccount,
  isDoctorRecruiterAccount,
  isCorporationAccount,
  isAuthor,
  loginUser,
  nonPublic,
  shouldLoginInduction,
}) => {
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  // マウント時にイベント送信
  useEffect(() => {
    const gaLabel =
      article.needLogin && !isAuthenticated
        ? 'need_login'
        : !isAuthor
        ? 'limited_share'
        : null;
    sendAnalyticsEvent('slide', 'show', gaLabel);
  }, [article.articleId, article.needLogin, isAuthenticated, isAuthor]);
  const bgColor = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const loginButtonColor = useColorModeValue('white', 'blackAlpha.600');

  const {
    liked: articleLiked,
    likeArticle,
    unlikeArticle,
  } = useArticleLike(initialLikes.article, article.articleId);
  const { pagesLiked, likePage, unlikePage } = usePageLike(
    initialLikes.pages,
    article.articleId,
  );
  const likes: Likes = {
    article: articleLiked,
    pages: pagesLiked,
  };

  const router = useAppRouter();
  const toast = useToast();
  useEffect(() => {
    if (isAuthenticated && router.query.action === 'like') {
      likeArticle().catch((_e) => {});
      router.replace(`/article/view/${article.articleId}`, undefined, {
        shallow: true,
      });
    }
  }, [isAuthenticated, router, likeArticle, article.articleId]);

  const {
    articleCommentCount,
    articleComments,
    getArticleCommentCount,
    fetchArticleComments,
    postArticleComment,
    deleteArticleComment,
    postArticleCommentReply,
    deleteArticleCommentReply,
  } = useArticleComment(article.articleId);
  useEffect(() => {
    getArticleCommentCount().catch((_e) => {});
    fetchArticleComments().catch((_e) => {}); // 非ログイン時は先頭1件のみが返される
  }, [getArticleCommentCount, fetchArticleComments, article.articleId]);

  const disclosure = useDisclosure();

  const achievedBadgeDisclosure = useDisclosure();
  const [badge, setBadge] = useState<BadgeArgs>();

  const [activeIndex, setActiveIndex] = useState(0);

  const special: Special | undefined = !isCorporationAccount
    ? findSpecialByTags(tags) ?? findSpecialByArticleId(article.articleId)
    : undefined;
  const mobileSpecialBar: MobileSpecialBar | undefined = !isCorporationAccount
    ? findMobileSpecialBarByTags(tags) ??
      findMobileSpecialBarByArticleId(article.articleId)
    : undefined;

  const handleLoginClick = () => {
    router.pushLogin({
      redirectUrl: `/article/view/${article.articleId}#${activeIndex + 1}`,
    });
  };

  const toArticleLikePage = () => {
    router.push('/mypage/like/articles');
  };

  const onClickArticleLike = async () => {
    if (!isAuthenticated) {
      toast({
        title: (
          <Text>
            スライド保存をするには
            <Link onClick={handleLoginClick}>ログイン</Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }
    if (!isLicenseVerified) {
      toast({
        title: (
          <Text>
            スライド保存をするには
            <Link
              onClick={async () => {
                // prettier-ignore
                sendAnalyticsEvent('induction', 'doctor_certification', 'from_slide_save');
                await proceedIdPage(
                  `/users/doctor/register?from_service=slide`,
                );
              }}
            >
              医師/医学生認証
            </Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }

    try {
      toast({
        title: (
          <Flex>
            <Text mr={{ base: 4, md: 10 }} fontSize={{ base: 11, md: 14 }}>
              スライドを保存しました
            </Text>
            <Link fontSize={{ base: 11, md: 14 }} onClick={toArticleLikePage}>
              保存したスライドを見る
            </Link>
          </Flex>
        ),
        status: 'success',
        isClosable: false,
        variant: 'subtle',
        position: 'top',
      });
      await likeArticle();
    } catch {
      toast({
        title: '保存に失敗しました。',
        description: 'しばらく待って再度お試しください。',
        status: 'error',
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  const onClickArticleUnlike = async () => {
    try {
      await unlikeArticle();
    } catch {
      toast({
        title: '保存の解除に失敗しました。',
        description: 'しばらく待って再度お試しください。',
        status: 'error',
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  const toLikePage = () => {
    router.push('/mypage/like/pages');
  };

  const onClickPageLike = async (pageNum: number) => {
    if (!isAuthenticated) {
      toast({
        title: (
          <Text>
            ページ保存をするには
            <Link onClick={handleLoginClick}>ログイン</Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }
    if (!isLicenseVerified) {
      toast({
        title: (
          <Text>
            ページ保存をするには
            <Link
              onClick={async () => {
                // prettier-ignore
                sendAnalyticsEvent('induction', 'doctor_certification', 'from_page_save');
                await proceedIdPage(
                  `/users/doctor/register?from_service=slide`,
                );
              }}
            >
              医師/医学生認証
            </Link>
            してください。
          </Text>
        ),
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
      });
      return;
    }

    try {
      await likePage(pageNum);
      toast({
        title: (
          <Flex>
            <Text mr={{ base: 4, md: 10 }} fontSize={{ base: 11, md: 14 }}>
              ページを保存しました
            </Text>
            <Link fontSize={{ base: 11, md: 14 }} onClick={toLikePage}>
              保存したページを見る
            </Link>
          </Flex>
        ),
        status: 'success',
        isClosable: false,
        variant: 'subtle',
        position: 'top',
      });
    } catch {
      toast({
        title: '保存に失敗しました。',
        description: 'しばらく待って再度お試しください。',
        status: 'error',
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  const onClickPageUnlike = async (pageNum: number) => {
    try {
      await unlikePage(pageNum);
    } catch {
      toast({
        title: '保存の解除に失敗しました。',
        description: 'しばらく待って再度お試しください。',
        status: 'error',
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  /* 同じタグの記事6件。足りなければ同じ診療科の記事で補完される。 */
  const relatedArticles = useMemo(() => {
    if (
      author.antaaAccountType === AntaaAccountType.CORPORATION &&
      author.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER
    ) {
      return authorOtherArticles;
    } else {
      return Array.from(
        new Map(
          [...sameTagArticles, ...sameDepartmentArticles].map((article) => [
            article.articleId,
            article,
          ]),
        ).values(),
      ).slice(0, MAX_RELATED_ARTICLES_COUNT);
    }
  }, [authorOtherArticles, sameTagArticles, sameDepartmentArticles]);

  if (nonPublic) {
    return nonPublicMessage();
  }
  if (shouldLoginInduction) {
    const { idBaseUrl } = getPublicRuntimeConfig();
    const accountSignupUrl = `${idBaseUrl}/signup?from_service=slide`;
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}`,
    );
    const loginUrl = `/login?redirectUrl=${redirectUrl}`;
    const signupUrl = `${accountSignupUrl}&redirect_url=${redirectUrl}`;

    return (
      <VStack>
        <Alert
          status="info"
          justifyContent="center"
          borderRadius="md"
          pt={10}
          pb={10}
          mb={4}
        >
          このページを閲覧するにはログインが必要です。
        </Alert>
        <Stack direction={['column', 'row']} spacing="20px" maxWidth="100%">
          <NextLink href={loginUrl} passHref>
            <Button
              as="a"
              colorScheme="brand"
              size="lg"
              width="400px"
              maxWidth="100%"
              onClick={() =>
                sendAnalyticsEvent('induction', 'login', 'need_login')
              }
            >
              ログイン
            </Button>
          </NextLink>
          <NextLink href={signupUrl} passHref>
            {/* アカウント登録は事前のID基盤ログインが不要なためproceedIdPageを使わない */}
            <Button
              as="a"
              bgColor={loginButtonColor}
              color="brand.400"
              borderColor="brand.400"
              borderWidth={1}
              size="lg"
              width="400px"
              maxWidth="100%"
              onClick={() =>
                sendAnalyticsEvent('induction', 'signup', 'need_login')
              }
            >
              アカウントをお持ちでない方はこちら
            </Button>
          </NextLink>
        </Stack>
      </VStack>
    );
  }

  if (article.status !== 'DONE') {
    switch (article.status) {
      case 'CONVERTING':
        return (
          <>
            <Alert status="info" display="flex" justifyContent="center">
              このスライドは変換中です。1ページあたり約8秒かかります。
              <br />
              しばらく待ってから、画面をリロードしてください。
            </Alert>
            {isAuthor && (
              <Alert status="warning" display="flex" justifyContent="center">
                <Text>
                  スライドを投稿してから30分以上経っても変換が完了しない場合、
                  <ExternalLink href={URL_INQUIRY_FORM_MULTI}>
                    こちら
                  </ExternalLink>
                  からお問い合わせください。
                </Text>
              </Alert>
            )}
          </>
        );
      case 'DELETED':
        return <Alert status="info">このスライドは削除されました。</Alert>;
      case 'ERROR':
      default:
        return (
          <Alert status="warning">
            このスライドの変換中にエラーが発生しました。
            <br />
            詳細はサポートにお問い合わせください。
          </Alert>
        );
    }
  }

  return (
    <>
      <AchievedBadgeModal badge={badge} disclosure={achievedBadgeDisclosure} />
      <PageHead
        author={author}
        article={article}
        departments={articleDepartments}
        richResultArticle={richResultArticle}
      />
      <Box pb={1} pl={{ base: 4, md: 0, lg: 0 }} pr={{ base: 4, md: 0, lg: 0 }}>
        <Breadcrumbs departments={articleDepartments} />
      </Box>
      <Box pl={{ base: 4, md: 0, lg: 0 }} pr={{ base: 4, md: 0, lg: 0 }}>
        {/* スライド */}
        {!!article.hidden && (
          <HiddenNotice
            article={article}
            isAdminAccount={isAdminAccount}
            isAuthor={isAuthor}
          />
        )}
        {!isAuthenticated && article.discloseScope === 'PARTIAL' && (
          <SignupLeadForPartialDisclose article={article} />
        )}
        {isAuthenticated &&
          !isLicenseVerified &&
          article.discloseScope === 'PARTIAL' && (
            <Text textAlign="center" mb={4}>
              このスライドは会員限定コンテンツです。
              <Box display={['inline', 'inline', 'none']} as="span">
                <br />
              </Box>
              今すぐ
              <Link
                sx={{ textDecoration: 'underline' }}
                onClick={async () => {
                  // prettier-ignore
                  sendAnalyticsEvent('induction', 'doctor_certification', 'from_members_only');
                  await proceedIdPage(
                    `/users/doctor/register?from_service=slide`,
                  );
                }}
              >
                医師/医学生認証
              </Link>
              して、続きを読みましょう！
            </Text>
          )}
        <Flex justify="flex-end">
          <ArticleEditButton
            article={article}
            isPharmaceuticalAccount={isPharmaceuticalAccount}
            mb={3}
          />
        </Flex>
        {/* スライド上表示 Antaa Slide Award 2023「つながる力」賞アンケート導線
        {author.antaaAccountType !== AntaaAccountType.CORPORATION && (
          <Flex justify="flex-end" display={['none', 'flex']}>
            <UsefulFormLinkUpper article={article} />
          </Flex>
        )}
        */}

        <Grid
          ml={{ base: -4, md: 0, lg: 0 }}
          mr={{ base: -4, md: 0, lg: 0 }}
          templateColumns="repeat(12, 1fr)"
          gap={4}
        >
          <GridItem colSpan={{ base: 12, md: 8, '2xl': 9 }}>
            <SlideViewer
              article={article}
              author={author}
              likes={likes}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              onClickPageLike={onClickPageLike}
              onClickPageUnlike={onClickPageUnlike}
              isDemoAccount={isDemoAccount}
              isPharmaceuticalAccount={isPharmaceuticalAccount}
              isDoctorRecruiterAccount={isDoctorRecruiterAccount}
              relatedArticles={relatedArticles.slice(0, 2)}
              getArticleCommentCount={getArticleCommentCount}
              fetchArticleComments={fetchArticleComments}
              postArticleComment={postArticleComment}
              achievedBadgeModalOnOpen={achievedBadgeDisclosure.onOpen}
              setBadge={setBadge}
            />
          </GridItem>
          <GridItem
            colSpan={{ base: 12, md: 4, '2xl': 3 }}
            display={['none', 'none', 'flex']}
          >
            <RightAside
              relatedArticles={relatedArticles.slice(
                0,
                MAX_RELATED_ARTICLES_COUNT,
              )}
              special={special}
            />
          </GridItem>
        </Grid>
        <ArticleNameWithIcon
          name={article.name}
          discloseScope={article.discloseScope}
        />
        <Flex mt={2} alignItems="center">
          <AuthorProfile author={author} awards={authorAwards} />

          <Flex
            direction="column"
            ms="auto"
            display={{ base: 'none', md: 'block' }}
          >
            {!isDemoAccount && !isPharmaceuticalAccount && (
              <>
                <HStack justify="flex-end" spacing={2}>
                  {/* SNSシェア */}
                  {!(
                    author.antaaAccountType === AntaaAccountType.CORPORATION &&
                    author.antaaAccountSubType ===
                      AntaaAccountSubType.PHARMACEUTICAL
                  ) && (
                    <>
                      <FacebookShareCircleButton
                        article={article}
                        gaEventLabel={'facebook'}
                      />
                      <TwitterShareCircleButton
                        author={author}
                        article={article}
                        gaEventLabel={'twitter'}
                      />
                      <ClipboardShareCircleButton
                        article={article}
                        gaEventLabel={'copy_url'}
                      />
                    </>
                  )}
                  {/* 保存/保存解除ボタン */}
                  {!isDoctorRecruiterAccount && (
                    <ArticleLikeOrUnlikeButton
                      author={author}
                      article={article}
                      liked={articleLiked}
                      onClickLike={onClickArticleLike}
                      onClickUnlike={onClickArticleUnlike}
                      disclosure={disclosure}
                      style={{
                        paddingLeft: 4,
                        paddingRight: 4,
                        marginLeft: 2,
                      }}
                    />
                  )}
                </HStack>
              </>
            )}
          </Flex>
        </Flex>
        <Flex justify="space-between" align="flex-start" mt={2}>
          <ArticleMetrics
            sessionCount={article.sessionCount}
            likeCount={article.likeCount}
            updateDateIsoString={article.updateDate}
          />
        </Flex>

        <Wrap mt={{ base: 4, md: 2 }} spacing={1} align="center">
          {/* 診療科一覧 */}
          {articleDepartments.map((department) => (
            <WrapItem key={department.id}>
              <DepartmentBadgeLink department={department} />
            </WrapItem>
          ))}

          {/* タグ一覧 */}
          {tags.map((tag) => (
            <WrapItem key={tag.id}>
              <TagButtonLink tag={tag} />
            </WrapItem>
          ))}
        </Wrap>
        {/* スライド下表示 Antaa Slide Award 2023「つながる力」賞アンケート導線
        {author.antaaAccountType !== AntaaAccountType.CORPORATION && (
          <Box mt={2} mb={-2}>
            <UsefulFormLink article={article} author={author} />
          </Box>
        )}
        */}

        <Flex
          direction="column"
          display={{ base: 'block', md: 'none' }}
          mt={2}
          position="relative"
          left={-2}
        >
          {!isDemoAccount && !isPharmaceuticalAccount && (
            <>
              <HStack spacing={2}>
                {/* SNSシェア */}
                {!(
                  author.antaaAccountType === AntaaAccountType.CORPORATION &&
                  author.antaaAccountSubType ===
                    AntaaAccountSubType.PHARMACEUTICAL
                ) && (
                  <>
                    <FacebookShareCircleButton
                      article={article}
                      gaEventLabel={'facebook'}
                    />
                    <TwitterShareCircleButton
                      author={author}
                      article={article}
                      gaEventLabel={'twitter'}
                    />
                  </>
                )}
                <ClipboardShareCircleButton
                  article={article}
                  gaEventLabel={'copy_url'}
                />
                {/* 保存/保存解除ボタン */}
                {!isDoctorRecruiterAccount && (
                  <Box ms="auto">
                    <ArticleLikeOrUnlikeButton
                      author={author}
                      article={article}
                      liked={articleLiked}
                      onClickLike={onClickArticleLike}
                      onClickUnlike={onClickArticleUnlike}
                      disclosure={disclosure}
                      style={{
                        paddingLeft: 4,
                        paddingRight: 4,
                        marginLeft: 2,
                        right: -2,
                      }}
                    />
                  </Box>
                )}
              </HStack>
            </>
          )}
        </Flex>

        <Box mt={8} borderRadius="lg" bgColor={bgColor}>
          <ArticleDescription
            article={article}
            targetReaderTypes={targetReaderTypes}
          />
        </Box>
        {/* コメント欄 */}
        {!(
          author.antaaAccountType === AntaaAccountType.CORPORATION &&
          author.antaaAccountSubType === AntaaAccountSubType.PHARMACEUTICAL
        ) &&
          !isPharmaceuticalAccount &&
          article.commentDisplayMode === commentDisplayMode.FULL && (
            <Box mt={4}>
              <ArticleComments
                loginUserId={loginUser?.userId ?? null}
                article={article}
                articleCommentCount={articleCommentCount}
                articleComments={articleComments}
                postArticleComment={postArticleComment}
                deleteArticleComment={deleteArticleComment}
                postArticleCommentReply={postArticleCommentReply}
                deleteArticleCommentReply={deleteArticleCommentReply}
                getArticleCommentCount={getArticleCommentCount}
                fetchArticleComments={fetchArticleComments}
                isDemoAccount={isDemoAccount}
                isDoctorRecruiterAccount={isDoctorRecruiterAccount}
                achievedBadgeModalOnOpen={achievedBadgeDisclosure.onOpen}
                setBadge={setBadge}
              />
            </Box>
          )}
        {/* 関連するスライド */}
        {relatedArticles.length > 0 && (
          <Box display={{ md: 'none' }}>
            <Divider mt={4} />
            <Box mt={4}>
              <RelatedArticles
                articles={relatedArticles.slice(
                  0,
                  MAX_RELATED_ARTICLES_MOBILE_COUNT,
                )}
                isMobile={isMobile}
              />
            </Box>
          </Box>
        )}
        {/* <投稿者>さんの他の投稿スライド */}
        {authorOtherArticles.length > 0 && (
          <>
            <Divider mt={4} />
            <Box mt={4}>
              <AuthorOtherArticles
                articles={authorOtherArticles}
                author={author}
                isMobile={isMobile}
              />
              {hasMoreAuthorOtherArticles && (
                <NextLink
                  href={`/profile/${author.discloseId || author.userId}`}
                  passHref
                >
                  <Button
                    as="a"
                    bg="white.500"
                    color="brand.500"
                    height={4}
                    fontSize={14}
                    _hover={{
                      bg: 'white.500',
                      textDecoration: 'underline',
                    }}
                    w="100%"
                    mt={3}
                  >
                    すべて見る
                  </Button>
                </NextLink>
              )}
            </Box>
          </>
        )}
        {/* このスライドと同じ診療科のスライド */}
        {!(
          author.antaaAccountType === AntaaAccountType.CORPORATION &&
          author.antaaAccountSubType === AntaaAccountSubType.PHARMACEUTICAL
        ) &&
          sameDepartmentArticles.length > 0 && (
            <>
              <Divider mt={4} />
              <Box mt={4}>
                <SameDepartmentArticles
                  articles={sameDepartmentArticles.slice(0, 6)} // MAX_SAME_DEPARTMENT_ARTICLES_COUNTを9にしているため6枚にする
                  isMobile={isMobile}
                />
              </Box>
            </>
          )}
        {/* テキスト全文 */}
        {!(
          author.antaaAccountType === AntaaAccountType.CORPORATION &&
          author.antaaAccountSubType === AntaaAccountSubType.PHARMACEUTICAL
        ) && (
          <>
            <Divider mt={4} />
            <Box mt={4} borderRadius="lg">
              <SlideTexts article={article} activeIndex={activeIndex} />
            </Box>
          </>
        )}
        {authorInterviews.length > 0 && (
          <>
            <Divider mt={4} />
            <Box mt={4}>
              <AuthorInterviews author={author} interviews={authorInterviews} />
            </Box>
          </>
        )}
        {isMobile && special && special?.mobileBannerImage && (
          <MobileSpecialBanner special={special as SpecialHasMobileBanner} />
        )}
      </Box>
      {isMobile ? (
        mobileSpecialBar ? (
          <MobileSpecialBar special={mobileSpecialBar} />
        ) : (
          <MobileAppDownloadBar />
        )
      ) : null}
    </>
  );
};

export default ArticleDetailPage;

const repository = {
  async findArticle(articleId: string) {
    return await prisma.article_header.findFirst({
      where: {
        article_id: articleId,
        is_deleted: false,
      },
      include: {
        article_page: true,
        article_references: true,
        article_table_of_contents: true,
      },
    });
  },

  async findHospital(hospitalId: number) {
    const hospital = await prisma.hospitals.findUnique({
      where: {
        id: hospitalId,
      },
      select: {
        name: true,
      },
    });
    return hospital ? hospital.name : null;
  },

  async findAuthor(authorId: number) {
    return await prisma.user.findUnique({
      where: {
        user_id: authorId,
      },
      select: {
        user_id: true,
        last_name: true,
        first_name: true,
        hospital_id: true,
        hospital: true,
        profile_photo_s3_key: true,
        update_date: true,
        twitter_account: true,
        disclose_id: true,
      },
    });
  },

  async findTagsByArticleId(articleId: string) {
    const tagIds = await prisma.article_tag_connection
      .findMany({
        where: {
          article_id: articleId,
        },
        select: {
          tag_id: true,
        },
      })
      .then((conns) => conns.map((conn) => conn.tag_id));

    return await prisma.tag.findMany({
      where: {
        tag_id: {
          in: tagIds,
        },
      },
    });
  },

  async findTagsByTagNames(tagNames: string[]) {
    return prisma.tag.findMany({
      where: {
        tag_name: {
          in: tagNames,
        },
      },
      select: {
        tag_id: true,
      },
    });
  },

  async findArticlesByTagIdsAndExcludeArticleId(
    tagIds: number[],
    articleId: string,
  ) {
    return await prisma.article_header
      .findMany({
        select: {
          article_id: true,
          name: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          NOT: {
            article_id: articleId,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
          article_tag_connection: {
            some: {
              tag_id: {
                in: tagIds,
              },
            },
          },
        },
      })
      .then((articles) =>
        articles.map((article) => ({
          ...article,
          commentCount: article.article_comments.length,
        })),
      );
  },

  async findArticlesByAuthorIdAndExcludeArticleId(
    authorId: number,
    articleId: string,
  ) {
    return await prisma.article_header
      .findMany({
        select: {
          article_id: true,
          name: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          AND: {
            author_id: authorId,
            status: 'DONE',
            hidden: false,
            is_deleted: false,
            NOT: {
              article_id: articleId,
            },
          },
        },
        take: MAX_AUTHOR_OTHER_ARTICLES_COUNT + 1, // 「もっとみる」ボタンの表示判定用
      })
      .then((articles) =>
        articles.map((article) => ({
          ...article,
          commentCount: article.article_comments.length,
        })),
      );
  },

  async findArticlesByArticleIds(articleIds: string[]) {
    return await prisma.article_header
      .findMany({
        select: {
          article_id: true,
          name: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIds,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
      })
      .then((articles) =>
        articles.map((article) => ({
          ...article,
          commentCount: article.article_comments.length,
        })),
      );
  },

  async findDepartmentIdsByArticleId(articleId: string) {
    return await prisma.article_has_departments
      .findMany({
        distinct: ['department_id'],
        select: {
          department_id: true,
        },
        where: {
          article_id: articleId,
        },
      })
      .then((records) => records.map((record) => record.department_id));
  },

  async findDepartmentsByArticleId(articleId: string) {
    return await prisma.departments.findMany({
      where: {
        article_has_departments: {
          some: {
            article_id: articleId,
          },
        },
      },
      select: {
        id: true,
        department_name_ja: true,
      },
    });
  },

  async findArticleLikeByArticleIdAndUserId(articleId: string, userId: number) {
    return await prisma.article_like.findUnique({
      where: {
        article_id_user_id: {
          article_id: articleId,
          user_id: userId,
        },
      },
    });
  },

  async findPageLikesByArticleIdAndUserId(articleId: string, userId: number) {
    return await prisma.page_like.findMany({
      where: {
        article_id: articleId,
        user_id: userId,
      },
    });
  },

  async findTargetReaderIdsConnectToArticle(articleId: string) {
    return await prisma.article_has_target_readers.findMany({
      select: {
        target_reader_type: {
          select: {
            id: true,
            name: true,
          },
        },
      },
      where: {
        article_id: articleId,
      },
      orderBy: {
        target_reader_type: {
          id: 'asc',
        },
      },
    });
  },

  async findAwardsByUserId(userId: number) {
    return await prisma.awards.findMany({
      where: {
        user_has_awards: {
          some: {
            user_id: userId,
          },
        },
      },
    });
  },

  async findInterviewsByUserId(userId: number) {
    return await prisma.interviews.findMany({
      where: {
        user_has_interviews: {
          some: {
            user_id: userId,
          },
        },
      },
    });
  },
};

const service = {
  shouldIncrementContentView(req: IncomingMessage) {
    return req.isUnauthenticated() || !hasManageAuthority(req.user!.userRoles);
  },

  async incrementContentView(articleId: string) {
    await prisma.article_header.update({
      where: {
        article_id: articleId,
      },
      data: {
        view_count: {
          increment: 1,
        },
      },
    });
  },

  async findArticle(articleId: string): Promise<Article | null> {
    const article = await repository.findArticle(articleId);
    if (!article) {
      return null;
    }

    const pages = article.article_page
      .map((p) => ({
        articleId: p.article_id,
        pageNumber: p.page_number,
        slideText: p.slide_text,
        linkUrl: p.link_url,
        imgL: p.img_l!,
        likeCount: p.like_count,
      }))
      .map((p) => ({
        ...p,
        imgL: p.imgL.startsWith('/') ? p.imgL.substring(1) : p.imgL,
      }))
      .slice(0, article.page_count || undefined);

    let video: ArticleVideo | null = null;
    if (article.article_type === 'VIDEO' && pages.length >= 2) {
      video = {
        fileName: pages[1].imgL,
      };
    }

    let tableOfContents: TableOfContents[] | null = null;
    if (article.article_table_of_contents) {
      const answer = JSON.parse(
        article.article_table_of_contents.contents_json,
      ).answer;
      tableOfContents = answer.map((a: any) => ({
        pageRange: a.page_range,
        contents: a.contents,
      }));
    }

    /** DateをYYYY/MM/DD形式の文字列に変換 */
    const formatDate = (date: Date): string => {
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    };

    return {
      articleId: article.article_id,
      authorId: article.author_id,
      name: article.name,
      description: article.description,
      status: article.status as ArticleStatus | null,
      type: article.article_type === 'VIDEO' ? 'VIDEO' : 'SLIDE',
      topImageM: article.top_img_m?.startsWith('/')
        ? article.top_img_m.substring(1)
        : article.top_img_m,
      topImageL: article.top_img_l?.startsWith('/')
        ? article.top_img_l.substring(1)
        : article.top_img_l,
      width: article.width,
      height: article.height,
      discloseScope: article.disclose_scope as DiscloseScope,
      pageCount: article.page_count || 0,
      sessionCount: article.session_count || 0,
      likeCount:
        article.like_count +
        pages.reduce((prev, curr) => prev + curr.likeCount, 0),
      createDate: date.toIso8601Jst(article.create_date),
      updateDate: date.toIso8601Jst(article.update_date),
      createDateFormatted: formatDate(article.create_date),
      updateDateFormatted: formatDate(article.update_date),
      updateDateEpochMilliseconds: article.update_date.getTime(),
      pages,
      video,
      highlight: article.highlight,
      references: article.article_references.map((ar) => ar.reference),
      hidden: article.hidden,
      needLogin: article.need_login,
      commentDisplayMode: article.comment_display_mode as CommentDisplayMode,
      tableOfContents,
    };
  },

  async findAuthor(authorId: number): Promise<Author | null> {
    const author = await repository.findAuthor(authorId);
    if (!author) {
      return null;
    }

    // アカウント統合済みの場合はID基盤側のプロフィール情報を表示させる
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(
      author.disclose_id ? [author.disclose_id] : [],
    );
    const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

    // アカウント統合完了時はID基盤側のプロフィールに設定されたhospital_idを用いるが
    // hospitalsのテーブルマスタ情報はSlideとID基盤（QA）で等しいため
    // Slide DBから取得する
    let hospitalName: string | null = null;
    if (idBaseProfile?.hospitalId) {
      const hospital = await prisma.hospitals.findUnique({
        where: {
          disclose_id: idBaseProfile.hospitalId,
        },
        select: {
          name: true,
        },
      });
      hospitalName = hospital?.name ?? null;
    }
    hospitalName ??= idBaseProfile?.hospitalName;
    // ID基盤側のIDでレコード取得できなかった場合はSlide側のIDでフォールバックさせる
    if (!hospitalName && author.hospital_id) {
      hospitalName = await repository.findHospital(author.hospital_id);
    }
    hospitalName ??= author.hospital;

    const profilePhotoKey =
      idBaseProfile?.profilePhotoUrl ?? author.profile_photo_s3_key;
    return {
      userId: author.user_id,
      userName: idBaseProfile?.userName ?? null,
      // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
      lastName: idBaseProfile?.userName
        ? null
        : idBaseProfile?.lastName ?? author.last_name,
      firstName: idBaseProfile?.userName
        ? null
        : idBaseProfile?.firstName ?? author.first_name,
      hospital: hospitalName,
      hasProfileImage: !!profilePhotoKey,
      idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
      updateDateEpochMilliseconds: author.update_date.getTime(),
      twitterAccount: idBaseProfile?.twitterAccount ?? author.twitter_account,
      discloseId: author.disclose_id,
      antaaAccountType: idBaseProfile?.accountType ?? null,
      antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
      isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
    };
  },

  async findTagsByArticleId(articleId: string): Promise<Tag[]> {
    const tags = await repository.findTagsByArticleId(articleId);
    return tags.map((tag) => ({
      id: tag.tag_id,
      name: tag.tag_name,
    }));
  },

  /**
   * 指定のスライドと同一タグをつけられたスライドの一覧を取得
   *
   * 並び順はランダム、取得件数は最大 {@link MAX_SAME_TAG_ARTICLES_COUNT} 件。
   */
  async findSameTagArticles(
    articleId: string,
    author: Author,
    isCorporationAccount: boolean,
  ): Promise<ArticleHeader[]> {
    const tags = await repository.findTagsByArticleId(articleId);
    let tagIds: number[] = tags.map((tag) => tag.tag_id);

    // 法人アカウントでは特集のバナーを表示しない
    // 特集ページのタグと一致するタグがあれば設定を取得
    const special = !isCorporationAccount
      ? findSpecialByTags(
          tags.map((tag) => ({
            id: tag.tag_id,
            name: tag.tag_name,
          })),
        )
      : undefined;
    const preferSameTags: boolean = !!special?.relatedSlidesPreferSpecialTags;
    // 特集のタグ配列のスライドを優先して表示する場合tagIdsの配列を上書き
    if (preferSameTags && special?.tags.length) {
      const relatedTagNames: string[] = special.tags;
      const relatedTags = await repository.findTagsByTagNames(relatedTagNames);
      tagIds = relatedTags.map((tag) => tag.tag_id);
    }

    const sameTagArticleEntities =
      await repository.findArticlesByTagIdsAndExcludeArticleId(
        tagIds,
        articleId,
      );

    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = sameTagArticleEntities
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    let sameTagArticles = sameTagArticleEntities.map((article) => {
      const idBaseProfile =
        idBaseProfileMap[
          article.user_article_header_author_idTouser.disclose_id ?? ''
        ];
      return {
        articleId: article.article_id,
        name: article.name,
        status: article.status as ArticleStatus | null,
        topImageM: article.top_img_m?.startsWith('/')
          ? article.top_img_m.substring(1)
          : article.top_img_m,
        sessionCount: article.session_count || 0,
        likeCount:
          article.like_count +
          article.article_page.reduce(
            (prev, curr) => prev + curr.like_count,
            0,
          ),
        discloseScope: article.disclose_scope as DiscloseScope | null,
        commentCount: article.commentCount,
        createDate: date.toIso8601Jst(article.create_date),
        updateDate: date.toIso8601Jst(article.update_date),
        author: {
          userId: article.user_article_header_author_idTouser.user_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ??
              article.user_article_header_author_idTouser.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ??
              article.user_article_header_author_idTouser.first_name,
          updateDateEpochMilliseconds:
            article.user_article_header_author_idTouser.update_date.getTime(),
          profilePhotoS3Key:
            article.user_article_header_author_idTouser.profile_photo_s3_key,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
        },
        tags: article.article_tag_connection.map((conn) => ({
          id: conn.tag.tag_id,
          name: conn.tag.tag_name,
        })),
      };
    });

    if (
      author.antaaAccountType === AntaaAccountType.CORPORATION &&
      author.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER
    ) {
      // 法人アカウント(医療機関以外)が投稿したスライド詳細表示時は同一アカウントが投稿したスライドのみに絞り込む
      sameTagArticles = sameTagArticles.filter(
        (article) => article.author.userId === author.userId,
      );
    } else {
      // 法人アカウント以外が投稿したスライド詳細表示時は法人アカウントが投稿したスライドを除外する
      sameTagArticles = sameTagArticles.filter(
        (article) =>
          article.author.antaaAccountType !== AntaaAccountType.CORPORATION ||
          article.author.antaaAccountSubType ===
            AntaaAccountSubType.DOCTOR_RECRUITER,
      );
    }

    if (preferSameTags) {
      // タグのスライドを優先して表示するのでランダマイズせずに取り出す
      sameTagArticles = sameTagArticles.slice(0, MAX_SAME_TAG_ARTICLES_COUNT);
    } else {
      // 指定個数分をランダムに取り出す
      // @see https://lodash.com/docs/4.17.15#sampleSize
      sameTagArticles = sampleSize(
        sameTagArticles,
        MAX_SAME_TAG_ARTICLES_COUNT,
      );
    }
    return sameTagArticles;
  },

  async findAuthorOtherArticles(
    authorId: number,
    articleId: string,
  ): Promise<ArticleHeader[]> {
    const articles = await repository.findArticlesByAuthorIdAndExcludeArticleId(
      authorId,
      articleId,
    );

    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articles
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    return articles.map((article) => {
      const idBaseProfile =
        idBaseProfileMap[
          article.user_article_header_author_idTouser.disclose_id ?? ''
        ];
      return {
        articleId: article.article_id,
        name: article.name,
        status: article.status as ArticleStatus | null,
        topImageM: article.top_img_m?.startsWith('/')
          ? article.top_img_m.substring(1)
          : article.top_img_m,
        sessionCount: article.session_count || 0,
        likeCount:
          article.like_count +
          article.article_page.reduce(
            (prev, curr) => prev + curr.like_count,
            0,
          ),
        discloseScope: article.disclose_scope as DiscloseScope | null,
        commentCount: article.commentCount,
        createDate: date.toIso8601Jst(article.create_date),
        updateDate: date.toIso8601Jst(article.update_date),
        author: {
          userId: article.user_article_header_author_idTouser.user_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ??
              article.user_article_header_author_idTouser.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ??
              article.user_article_header_author_idTouser.first_name,
          updateDateEpochMilliseconds:
            article.user_article_header_author_idTouser.update_date.getTime(),
          profilePhotoS3Key:
            article.user_article_header_author_idTouser.profile_photo_s3_key,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
        },
        tags: article.article_tag_connection.map((conn) => ({
          id: conn.tag.tag_id,
          name: conn.tag.tag_name,
        })),
      };
    });
  },

  /**
   * 指定のスライドと同一診療科を持つスライドの一覧を取得
   *
   * 並び順は、1) 指定のスライドとの重複診療科数（多い順）、2) 更新日順（新しい順）とする。
   * 取得件数は、最大 {@link MAX_SAME_DEPARTMENT_ARTICLES_COUNT} 件。
   */
  async findSameDepartmentArticles(
    articleId: string,
    author: Author,
  ): Promise<ArticleHeader[]> {
    // step1:
    // 指定のスライドの診療科ID一覧を取得
    const departmentIds = await repository.findDepartmentIdsByArticleId(
      articleId,
    );

    if (departmentIds.length === 0) {
      return [];
    }

    // step2:
    // 指定のスライドと同一診療科を持つスライドのID一覧を取得
    // 並び順は、1) 指定のスライドとの重複診療科数（多い順）、2) 更新日順（新しい順）とする
    const sameDepartmentArticleIdObjects = await prisma.$queryRaw<
      { articleId: string }[]
    >`
        SELECT a.article_id AS articleId
        FROM article_header a,
             article_has_departments d
        WHERE a.article_id = d.article_id
          AND d.department_id IN (${Prisma.join(departmentIds)})
          AND a.article_id <> ${articleId}
          AND a.status = 'DONE'
          AND a.hidden = 0
          AND a.is_deleted = 0
        GROUP BY a.article_id,
                 a.update_date
        ORDER BY COUNT(a.article_id) DESC,
                 a.update_date DESC
            LIMIT ${MAX_SAME_DEPARTMENT_ARTICLES_COUNT}
    `;

    const sameDepartmentArticleIds = sameDepartmentArticleIdObjects.map(
      (obj) => obj.articleId,
    );

    // step3:
    // スライド一覧表示用のデータを取得
    const articlesWithCommentCount = await repository.findArticlesByArticleIds(
      sameDepartmentArticleIds,
    );

    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articlesWithCommentCount
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    let articles = articlesWithCommentCount.map((article) => {
      const idBaseProfile =
        idBaseProfileMap[
          article.user_article_header_author_idTouser.disclose_id ?? ''
        ];
      return {
        articleId: article.article_id,
        name: article.name,
        status: article.status as ArticleStatus | null,
        topImageM: article.top_img_m?.startsWith('/')
          ? article.top_img_m.substring(1)
          : article.top_img_m,
        sessionCount: article.session_count || 0,
        likeCount:
          article.like_count +
          article.article_page.reduce(
            (prev, curr) => prev + curr.like_count,
            0,
          ),
        discloseScope: article.disclose_scope as DiscloseScope | null,
        commentCount: article.commentCount,
        createDate: date.toIso8601Jst(article.create_date),
        updateDate: date.toIso8601Jst(article.update_date),
        author: {
          userId: article.user_article_header_author_idTouser.user_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ??
              article.user_article_header_author_idTouser.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ??
              article.user_article_header_author_idTouser.first_name,
          updateDateEpochMilliseconds:
            article.user_article_header_author_idTouser.update_date.getTime(),
          profilePhotoS3Key:
            article.user_article_header_author_idTouser.profile_photo_s3_key,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
        },
        tags: article.article_tag_connection.map((conn) => ({
          id: conn.tag.tag_id,
          name: conn.tag.tag_name,
        })),
      };
    });

    if (
      author.antaaAccountType === AntaaAccountType.CORPORATION &&
      author.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER
    ) {
      // 法人アカウントが投稿したスライド詳細表示時は同一アカウントが投稿したスライドのみに絞り込む
      articles = articles.filter(
        (article) => article.author.userId === author.userId,
      );
    } else {
      // 法人アカウント以外が投稿したスライド詳細表示時は法人アカウントが投稿したスライドを除外する
      articles = articles.filter(
        (article) =>
          article.author.antaaAccountType !== AntaaAccountType.CORPORATION &&
          article.author.antaaAccountSubType ===
            AntaaAccountSubType.DOCTOR_RECRUITER,
      );
    }

    // step4:
    // step3のデータを、step2のスライドID一覧と同じ順に並び替える
    const sortedArticles = sameDepartmentArticleIds.reduce<ArticleHeader[]>(
      (prev, curr) => {
        const article = articles.find((article) => article.articleId === curr);
        if (!article) {
          // articleがundefinedになることはないが、安全のためundefinedチェックを入れておく
          return prev;
        }
        return prev.concat(article);
      },
      [],
    );

    return sortedArticles;
  },

  async findLikes(
    articleId: string,
    userId: number,
    pageCount: number,
  ): Promise<Likes> {
    const articleLike = await repository.findArticleLikeByArticleIdAndUserId(
      articleId,
      userId,
    );
    const pageLikes = await repository.findPageLikesByArticleIdAndUserId(
      articleId,
      userId,
    );
    return {
      article: !!articleLike,
      pages: pageLikes.reduce((prev, curr) => {
        prev[curr.page_number - 1] = true;
        return prev;
      }, [...Array(pageCount)].fill(false)),
    };
  },

  /**
   * 指定のスライドに紐づいた診療科一覧を取得
   */
  async findDepartmentsByArticleId(articleId: string): Promise<Department[]> {
    return await repository
      .findDepartmentsByArticleId(articleId)
      .then((departments) =>
        departments.map((department) => ({
          id: department.id,
          name: department.department_name_ja,
        })),
      );
  },

  /**
   * 診療科一覧を取得
   *
   * 診療科ごとに、IDと日本語表示名に加え、紐づいているスライド数も付加する。
   */
  async findAllDepartments(): Promise<DepartmentWithArticlesCount[]> {
    const departments = await prisma.$queryRaw<DepartmentWithArticlesCount[]>`
        SELECT d.id                   AS id,
               d.department_name_ja   AS name,
               count(a.department_id) AS articlesCount
        FROM departments d
                 LEFT JOIN article_has_departments a ON d.id = a.department_id
        WHERE d.is_deleted = 0
        GROUP BY d.id,
                 d.department_name_ja
        ORDER BY d.display_order ASC
    `;
    return departments.map((department) => {
      return {
        ...department,
        id: Number(department.id),
        articlesCount: Number(department.articlesCount),
      };
    });
  },

  /**
   * 指定のスライドに紐づいた対象読者一覧を取得
   */
  async findTargetReaderIdsConnectToArticle(
    articleId: string,
  ): Promise<TargetReaderType[]> {
    return await repository
      .findTargetReaderIdsConnectToArticle(articleId)
      .then((targetReaderTypes) =>
        targetReaderTypes.map((targetReaderType) => ({
          id: targetReaderType.target_reader_type.id,
          name: targetReaderType.target_reader_type.name,
        })),
      );
  },

  /**
   * リッチリザルトを作成
   */
  createRichResult({
    article,
    author,
  }: {
    article: Article;
    author: Author;
  }): RichResultArticle {
    return {
      headline: article.name || '',
      datePublished: article.createDate,
      dateModified: article.updateDate,
      image: article.pages
        .slice(0, 3)
        .map((page) => page.imgL)
        .map(
          (imageName) =>
            createArticleImageUrl(article.articleId, imageName, {
              version: article.updateDateEpochMilliseconds,
            })!,
        ),
      authorId: author.userId,
      authorDiscloseId: author.discloseId,
      authorName: createUserName(author),
    };
  },

  async findAwardsByUserId(userId: number): Promise<Award[]> {
    return await repository.findAwardsByUserId(userId).then((awards) =>
      awards.map((award) => ({
        id: award.id,
        name: award.name,
        year: award.year,
        imageFilename: award.image_filename,
      })),
    );
  },

  async findInterviewsByUserId(userId: number): Promise<Interview[]> {
    return await repository.findInterviewsByUserId(userId).then((interviews) =>
      interviews.map((interview) => ({
        id: interview.id,
        siteUrl: interview.site_url,
        imageUrl: interview.image_url,
      })),
    );
  },
};

async function verifyAccessKey(articleId: string, accessKey: string) {
  const verification = await prisma.article_limited_access.findFirst({
    where: {
      article_id: articleId,
      access_key: accessKey,
    },
  });

  return !!verification;
}

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const articleId = yup.string().validateSync(context.params?.articleId);
    if (!articleId) {
      return {
        notFound: true,
      };
    }

    // スライド編集によるURL変更のリダイレクト対応
    const redirectDestination = redirectPageMap.get(articleId);
    if (redirectDestination) {
      return {
        redirect: {
          permanent: false,
          destination: `/article/view/${redirectDestination}`,
        },
      };
    }

    const article = await service.findArticle(articleId);
    if (!article) {
      return {
        notFound: true,
      };
    }

    const author = await service.findAuthor(article.authorId);
    if (!author) {
      context.req.log.error('author not found');
      return {
        notFound: true,
      };
    }

    let nonPublic: boolean = false;
    const isAuthor = author.userId === context.req.user?.id;
    if (
      article.hidden &&
      !isAuthor &&
      !hasManageAuthority(context.req.user?.userRoles)
    ) {
      const accessKey = yup.string().validateSync(context.query?.key);
      if (
        context.req.isUnauthenticated() ||
        !accessKey ||
        !(await verifyAccessKey(articleId, accessKey))
      ) {
        context.req.log.error('hidden except author');
        if (context.req.isUnauthenticated()) {
          return {
            notFound: true,
          };
        }
        if (!accessKey || !(await verifyAccessKey(articleId, accessKey))) {
          nonPublic = true;
        }
      }
    }

    const shouldLoginInduction =
      !context.req.isAuthenticated() &&
      (!!article.needLogin ||
        (author?.antaaAccountType === AntaaAccountType.CORPORATION &&
          author?.antaaAccountSubType === AntaaAccountSubType.PHARMACEUTICAL));

    if (
      !shouldLoginInduction &&
      shouldNotShow(
        author,
        context.req.user,
        context.req.isAuthenticated(),
        article.discloseScope,
      )
    ) {
      context.req.log.error('hidden except account type');
      return {
        notFound: true,
      };
    }

    if (!nonPublic && service.shouldIncrementContentView(context.req)) {
      service
        .incrementContentView(articleId)
        .catch((e) => context.req.log.warn(e));
    }
    const isCorporationAccount =
      context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION;

    const [
      tags,
      unspecifiedCountAuthorOtherArticles,
      sameTagArticles,
      sameDepartmentArticles,
    ] = await Promise.all([
      service.findTagsByArticleId(articleId),
      service.findAuthorOtherArticles(author.userId, articleId),
      author.antaaAccountType === AntaaAccountType.CORPORATION &&
      author.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER
        ? []
        : service.findSameTagArticles(articleId, author, isCorporationAccount),
      author.antaaAccountType === AntaaAccountType.CORPORATION &&
      author.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER
        ? []
        : service.findSameDepartmentArticles(articleId, author),
    ]);

    const hasMoreAuthorOtherArticles =
      unspecifiedCountAuthorOtherArticles.length >
      MAX_AUTHOR_OTHER_ARTICLES_COUNT;

    const authorOtherArticles = unspecifiedCountAuthorOtherArticles.slice(
      0,
      MAX_AUTHOR_OTHER_ARTICLES_COUNT,
    );

    let likes: Likes = { article: false, pages: [] };
    let loginUser: { userId: number; discloseId: string | null } | null = null;
    if (context.req.isAuthenticated()) {
      likes = await service.findLikes(
        articleId,
        context.req.user.id,
        article.pageCount,
      );
      const user = await prisma.user.findUnique({
        where: {
          user_id: context.req.user!.id,
        },
        select: {
          user_id: true,
          last_name: true,
          first_name: true,
          profile_photo_s3_key: true,
          update_date: true,
          disclose_id: true,
        },
      });
      if (user) {
        loginUser = {
          userId: user.user_id,
          discloseId: user.disclose_id,
        };
      }
    }

    const [
      articleDepartments,
      allDepartments,
      targetReaderTypes,
      authorAwards,
      authorInterviews,
    ] = await Promise.all([
      service.findDepartmentsByArticleId(articleId),
      service.findAllDepartments(),
      service.findTargetReaderIdsConnectToArticle(articleId),
      service.findAwardsByUserId(author.userId),
      service.findInterviewsByUserId(author.userId),
    ]);

    const richResultArticle = service.createRichResult({ article, author });

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isAdminAccount = hasManageAuthority(context.req.user?.userRoles);
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        article,
        tags,
        author,
        authorAwards,
        authorInterviews,
        authorOtherArticles,
        hasMoreAuthorOtherArticles,
        sameTagArticles,
        sameDepartmentArticles,
        likes,
        articleDepartments,
        allDepartments,
        targetReaderTypes,
        richResultArticle,
        isMobile,
        isAdminAccount,
        isDemoAccount,
        isPharmaceuticalAccount: isPharmaceuticalAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isCorporationAccount,
        isAuthor,
        loginUser,
        nonPublic,
        shouldLoginInduction,
      },
    };
  },
);
